/**
 * Call Centre Dashboard
 * Main agent interface for handling calls, viewing customer info, and creating orders
 * Uses Slack authentication for agents
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Avatar,
  Stack,
  Autocomplete
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  ShoppingCart as CartIcon,
  History as HistoryIcon,
  Voicemail as VoicemailIcon,
  Send as SendIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  Check as CheckIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Forward10 as Forward10Icon,
  Replay10 as Replay10Icon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  QrCode2 as QrCodeIcon,
  PhoneAndroid as MobileIcon,
  Settings as SettingsIcon,
  GroupAdd as GroupAddIcon,
  Delete as DeleteIcon,
  PhoneMissed as PhoneMissedIcon,
  LocalOffer as TagIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import callCentreService from '../../services/callCentreService';
import Softphone from './Softphone';
import { useAuth } from '../../context/AuthContext';
import { MODULES, hasModuleAccess } from '../../config/modules';
import { getCallbackWindow } from '../../utils/callbackWindow';
import { totalsByCurrency } from '../../utils/currencies';

// Callback window badge — shows best UTC time to call back based on customer's phone country
const CallbackWindowBadge = ({ phone }) => {
  const cbw = getCallbackWindow(phone);
  if (!cbw) return null;
  return (
    <Tooltip title={`${cbw.country} business hours${cbw.isNowGood ? ' — good time to call now' : ' — outside business hours'}`}>
      <Chip
        size="small"
        icon={<AccessTimeIcon sx={{ fontSize: '0.7rem !important' }} />}
        label={cbw.label}
        sx={{
          height: 18,
          fontSize: '0.6rem',
          bgcolor: cbw.isNowGood ? '#e8f5e9' : '#ffebee',
          color: cbw.isNowGood ? '#2e7d32' : '#c62828',
          border: 'none',
          '& .MuiChip-icon': { color: cbw.isNowGood ? '#4caf50' : '#ef5350' }
        }}
      />
    </Tooltip>
  );
};

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const CallCentreDashboard = () => {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const softphoneRef = useRef(null);
  const { permissions } = useAuth();

  // Check if user has call centre management access
  const hasCallCentreAccess = hasModuleAccess(permissions, MODULES.CALL_CENTRE);

  // Helper to update URL params without losing existing ones
  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  // Click-to-dial function (optional callerId to use the original number the call came in on)
  const handleClickToCall = (phoneNumber, callerId) => {
    if (softphoneRef.current && softphoneRef.current.isReady()) {
      if (callerId && softphoneRef.current.makeCallWithCallerId) {
        softphoneRef.current.makeCallWithCallerId(phoneNumber, callerId);
      } else {
        softphoneRef.current.makeCall(phoneNumber);
      }
    } else {
      console.log('[Dashboard] Softphone not ready for outbound call');
      alert('Softphone is not ready. Please wait for it to connect.');
    }
  };

  // Agent state
  const [agentInfo, setAgentInfo] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [slackUserId, setSlackUserId] = useState('');

  // Customer state
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchType, setSearchType] = useState('phone');
  const [customer, setCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerPreview, setCustomerPreview] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerCalls, setCustomerCalls] = useState([]);
  const [customerEdeskTickets, setCustomerEdeskTickets] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Call state
  const [calls, setCalls] = useState([]);
  const [callStats, setCallStats] = useState(null);
  const [voicemails, setVoicemails] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [pendingCalls, setPendingCalls] = useState([]);
  const [brandPhoneNumbers, setBrandPhoneNumbers] = useState([]);
  const [onlineAgents, setOnlineAgents] = useState([]);
  const [activeCallbackId, setActiveCallbackId] = useState(null);

  // Order builder state
  const [productSearch, setProductSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [draftOrder, setDraftOrder] = useState(null);
  const [sendingOrder, setSendingOrder] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' or 'fixed_amount'
  const [discountValue, setDiscountValue] = useState('');

  // UI state
  const [mainTab, setMainTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null); // URL of currently playing audio
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1x, 1.5x, 2x
  const [audioProgress, setAudioProgress] = useState(0); // Current time in seconds
  const [audioDuration, setAudioDuration] = useState(0); // Total duration
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Mobile setup state
  const [mobileSetupExpanded, setMobileSetupExpanded] = useState(false);
  const [sipQrCode, setSipQrCode] = useState(null);
  const [sipCredentials, setSipCredentials] = useState(null);
  const [loadingSipQr, setLoadingSipQr] = useState(false);

  // Agent management state (admin only)
  const [agentManagementOpen, setAgentManagementOpen] = useState(false);
  const [agentAssignments, setAgentAssignments] = useState([]);
  const [slackUsers, setSlackUsers] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [loadingAgentData, setLoadingAgentData] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedBrandToAdd, setSelectedBrandToAdd] = useState('');
  const [addingAssignment, setAddingAssignment] = useState(false);
  const [assignmentFilterBrand, setAssignmentFilterBrand] = useState('');
  const [assignmentFilterAgent, setAssignmentFilterAgent] = useState('');
  const [assignmentFilterStatus, setAssignmentFilterStatus] = useState('active');
  const [assignmentSortBy, setAssignmentSortBy] = useState('brand');
  const [assignmentSortDir, setAssignmentSortDir] = useState('asc');

  // Expanded call state (for showing transcript)
  const [expandedCallId, setExpandedCallId] = useState(null);

  // Tags & Notes state for expanded calls
  const [callTags, setCallTags] = useState({}); // { callId: [tags] }
  const [callNotes, setCallNotes] = useState({}); // { callId: [notes] }
  const [newNoteText, setNewNoteText] = useState('');
  const [newTagInput, setNewTagInput] = useState('');
  const [popularTags, setPopularTags] = useState([]);
  const [addingNote, setAddingNote] = useState(false);
  const [addingTag, setAddingTag] = useState(false);

  // Incoming call banner state
  const [incomingCallInfo, setIncomingCallInfo] = useState(null);

  // All Calls dialog state
  const [allCallsDialogOpen, setAllCallsDialogOpen] = useState(false);
  const [allCallsFilter, setAllCallsFilter] = useState('all'); // all, completed, missed
  const [allCallsAgentFilter, setAllCallsAgentFilter] = useState(''); // agent slack user id
  const [allCalls, setAllCalls] = useState([]);
  const [allCallsPage, setAllCallsPage] = useState(1);
  const [allCallsLimit, setAllCallsLimit] = useState(50); // page size
  const [allCallsTotal, setAllCallsTotal] = useState(0);
  const [allCallsLoading, setAllCallsLoading] = useState(false);
  const [allCallsSortField, setAllCallsSortField] = useState('started_at'); // sort field
  const [allCallsSortDir, setAllCallsSortDir] = useState('desc'); // asc or desc
  const [allCallsExpandedId, setAllCallsExpandedId] = useState(null); // for viewing transcript in dialog
  const [allCallsPhoneSearch, setAllCallsPhoneSearch] = useState('');
  const phoneSearchTimerRef = useRef(null);
  const [agentsList, setAgentsList] = useState([]); // list of agents for filter dropdown

  // Check for agent session on mount
  useEffect(() => {
    checkSession();
    checkPushSupport();

    // Auto-refresh agent JWT every 24 hours to prevent expiry (7d TTL)
    const refreshInterval = setInterval(() => {
      if (localStorage.getItem('agentToken')) {
        callCentreService.refreshAgentSession().catch(err => {
          console.log('[CallCentre] Auto-refresh failed (non-critical):', err.message);
        });
      }
    }, 24 * 60 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  // Check if push notifications are supported AND actually subscribed
  const checkPushSupport = async () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    console.log('[Push] Support check:', {
      serviceWorker: 'serviceWorker' in navigator,
      PushManager: 'PushManager' in window,
      Notification: 'Notification' in window,
      supported
    });
    setPushSupported(supported);

    if (supported && Notification.permission === 'granted') {
      // Check if there's actually an active push subscription (not just permission)
      try {
        const registration = await navigator.serviceWorker.getRegistration('/call-centre-sw.js');
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            setPushEnabled(true);
            console.log('[Push] Existing push subscription found');
          } else {
            console.log('[Push] Permission granted but no push subscription - need to re-subscribe');
            setPushEnabled(false);
          }
        } else {
          console.log('[Push] No service worker registered - need to set up push');
          setPushEnabled(false);
        }
      } catch (err) {
        console.log('[Push] Error checking subscription:', err);
        setPushEnabled(false);
      }
    }
  };

  // Register for push notifications
  const enablePushNotifications = async () => {
    console.log('[Push] enablePushNotifications called', { pushSupported, pushEnabled });

    if (!pushSupported) {
      alert('Push notifications are not supported in this browser');
      return;
    }

    try {
      console.log('[Push] Requesting notification permission...');
      // Request notification permission
      const permission = await Notification.requestPermission();
      console.log('[Push] Permission result:', permission);
      if (permission !== 'granted') {
        alert('Notification permission denied');
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/call-centre-sw.js');
      console.log('[Push] Service worker registered');

      // Unsubscribe from any existing subscription (in case VAPID keys changed)
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('[Push] Unsubscribing from old subscription...');
        await existingSubscription.unsubscribe();
      }

      // Get VAPID public key from backend
      const vapidResponse = await callCentreService.getVapidPublicKey();
      if (!vapidResponse.success || !vapidResponse.publicKey) {
        throw new Error('Failed to get VAPID public key');
      }

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidResponse.publicKey)
      });

      // Send subscription to backend
      await callCentreService.savePushSubscription({
        subscription: subscription.toJSON(),
        slackUserId: slackUserId
      });

      setPushEnabled(true);
      console.log('[Push] Push notifications enabled');
    } catch (err) {
      console.error('[Push] Error enabling push notifications:', err);
      alert('Failed to enable push notifications: ' + err.message);
    }
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Fetch SIP QR code for mobile setup
  const fetchSipQrCode = async () => {
    if (loadingSipQr) return;

    setLoadingSipQr(true);
    try {
      const response = await callCentreService.getSipQrCode();
      if (response.success) {
        setSipQrCode(response.qrCode);
        setSipCredentials(response.credentials);
      }
    } catch (err) {
      console.error('[CallCentre] Error fetching SIP QR code:', err);
    } finally {
      setLoadingSipQr(false);
    }
  };

  // Load SIP credentials when mobile setup is expanded
  useEffect(() => {
    if (mobileSetupExpanded && !sipCredentials) {
      fetchSipQrCode();
    }
  }, [mobileSetupExpanded]);

  // Load agent management data when dialog opens
  const loadAgentManagementData = async () => {
    setLoadingAgentData(true);
    try {
      const [assignmentsRes, usersRes, brandsRes] = await Promise.all([
        callCentreService.getAgentBrands(),
        callCentreService.getSlackUsers(),
        callCentreService.getCompanyBrands()
      ]);

      if (assignmentsRes.success) setAgentAssignments(assignmentsRes.assignments || []);
      if (usersRes.success) setSlackUsers(usersRes.users || []);
      if (brandsRes.success) setAllBrands(brandsRes.brands || []);
    } catch (err) {
      console.error('[CallCentre] Error loading agent management data:', err);
    } finally {
      setLoadingAgentData(false);
    }
  };

  useEffect(() => {
    if (agentManagementOpen) {
      loadAgentManagementData();
    }
  }, [agentManagementOpen]);

  // Add agent-brand assignment
  const handleAddAssignment = async () => {
    if (!selectedAgent || !selectedBrandToAdd) return;

    setAddingAssignment(true);
    try {
      const result = await callCentreService.assignAgentToBrand({
        slackUserId: selectedAgent,
        brandId: parseInt(selectedBrandToAdd),
        priority: 1
      });

      if (result.success) {
        await loadAgentManagementData();
        setSelectedAgent('');
        setSelectedBrandToAdd('');
      }
    } catch (err) {
      console.error('[CallCentre] Error adding assignment:', err);
      alert('Failed to add assignment: ' + err.message);
    } finally {
      setAddingAssignment(false);
    }
  };

  // Remove agent-brand assignment
  const handleRemoveAssignment = async (assignmentId) => {
    if (!await confirm({ title: 'Remove Agent', message: 'Remove this agent from this brand?', confirmText: 'Remove', severity: 'warning' })) return;

    try {
      const result = await callCentreService.removeAgentBrand(assignmentId);
      if (result.success) {
        await loadAgentManagementData();
      }
    } catch (err) {
      console.error('[CallCentre] Error removing assignment:', err);
      alert('Failed to remove: ' + err.message);
    }
  };

  const checkSession = async () => {
    setLoading(true);

    // First check for agent token (Slack auth)
    const agentToken = localStorage.getItem('agentToken');
    // Also check for JWT token (needed to load all company brands)
    const jwtToken = localStorage.getItem('token');

    console.log('[CallCentre] Session check - agentToken:', !!agentToken, 'jwtToken:', !!jwtToken);

    if (agentToken) {
      try {
        // Verify token with backend
        console.log('[CallCentre] Verifying agent token...');
        const response = await callCentreService.verifyAgentToken();

        if (response.success && response.agent) {
          const agent = response.agent;
          console.log('[CallCentre] Agent verified:', agent);
          console.log('[CallCentre] Agent brands from verify:', agent.brands);
          setSlackUserId(agent.slackUserId);

          // If user also has JWT token, load ALL company brands for dropdown
          // Otherwise fall back to assigned brands only
          let brandsToUse = agent.brands || [];
          console.log('[CallCentre] Initial brandsToUse:', brandsToUse);

          if (jwtToken) {
            try {
              const brandsResponse = await fetch(
                `${process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'}/api/brands`,
                { headers: { Authorization: `Bearer ${jwtToken}` } }
              );
              const brandsData = await brandsResponse.json();
              console.log('[CallCentre] Loaded all company brands:', brandsData);

              if (brandsData.brands && brandsData.brands.length > 0) {
                brandsToUse = brandsData.brands;
              }
            } catch (brandErr) {
              console.log('[CallCentre] Could not load all brands, using assigned:', brandErr.message);
            }
          }

          const finalAgentInfo = {
            ...agent,
            brands: brandsToUse,
            assignedBrands: agent.brands || []
          };
          console.log('[CallCentre] Setting agentInfo:', finalAgentInfo);
          console.log('[CallCentre] Brands count:', brandsToUse.length);
          setAgentInfo(finalAgentInfo);

          // Select brand from URL param or default to first brand
          if (brandsToUse.length > 0) {
            const brandIdFromUrl = searchParams.get('brandId');
            let brandToSelect = brandsToUse[0];
            if (brandIdFromUrl) {
              const foundBrand = brandsToUse.find(b => b.id === parseInt(brandIdFromUrl));
              if (foundBrand) {
                brandToSelect = foundBrand;
              }
            }
            console.log('[CallCentre] Setting selectedBrand to:', brandToSelect);
            setSelectedBrand(brandToSelect);
          }

          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Token verification failed:', err);
        // Token invalid, clear it
        localStorage.removeItem('agentToken');
        localStorage.removeItem('agentInfo');
      }
    }

    // Check for regular JWT token (frontend login users)
    // jwtToken already declared above
    if (jwtToken) {
      try {
        // Parse the JWT to get user info
        const tokenParts = jwtToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.user) {
            // User has frontend login - set up agent info from JWT
            // Use real Slack ID from JWT if available, otherwise use empty string
            // (backend will lookup Slack ID by email when generating token)
            const userSlackId = payload.user.slack_user_id || '';

            // Set slackUserId state - use 'email_lookup' as placeholder to trigger softphone render
            // The token endpoint will lookup the real Slack ID by email
            setSlackUserId(userSlackId || 'email_lookup');

            // Build base agent info from JWT
            // Check admin status from JWT token - also fetch from /api/users/me for latest
            let isAdmin = payload.user.is_company_admin === true || payload.user.role === 'admin';
            console.log('[CallCentre] JWT payload admin check:', {
              is_company_admin: payload.user.is_company_admin,
              role: payload.user.role,
              isAdmin
            });

            // Also fetch user info from backend to get most up-to-date admin status
            try {
              const meResponse = await fetch(
                `${process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'}/api/users/me`,
                { headers: { Authorization: `Bearer ${jwtToken}` } }
              );
              if (meResponse.ok) {
                const meData = await meResponse.json();
                console.log('[CallCentre] /api/users/me response:', meData);
                if (meData.user?.isCompanyAdmin === true || meData.user?.role === 'admin') {
                  isAdmin = true;
                }
              }
            } catch (meErr) {
              console.log('[CallCentre] /api/users/me optional:', meErr.message);
            }

            const baseAgentInfo = {
              type: 'user',
              email: payload.user.email,
              name: payload.user.email.split('@')[0],
              slackUserId: userSlackId,
              userId: payload.user.id,
              companyId: payload.user.company_id,
              isAdmin: isAdmin,
              brands: []
            };

            // For JWT users (logged in via frontend), load ALL company brands for the dropdown
            // Assigned brands (crm_agent_brands) are only used for call routing, not for viewing data
            let allBrands = [];
            try {
              // First get ALL brands for the company (for dropdown)
              const brandsResponse = await fetch(
                `${process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'}/api/brands`,
                { headers: { Authorization: `Bearer ${jwtToken}` } }
              );
              const brandsData = await brandsResponse.json();
              console.log('[CallCentre] Loaded brands:', brandsData);

              // Handle both array response and {brands: [...]} response
              if (Array.isArray(brandsData) && brandsData.length > 0) {
                allBrands = brandsData;
              } else if (brandsData.brands && brandsData.brands.length > 0) {
                allBrands = brandsData.brands;
              }
            } catch (brandErr) {
              console.error('[CallCentre] Error loading brands:', brandErr);
            }

            // Also get agent info for Slack user ID (needed for softphone)
            try {
              const agentResponse = await callCentreService.getAgentInfo(userSlackId || 'email_lookup');
              if (agentResponse.success && agentResponse.agent) {
                // Update slackUserId if we got the real one from backend
                if (agentResponse.agent.slackUserId) {
                  setSlackUserId(agentResponse.agent.slackUserId);
                  baseAgentInfo.slackUserId = agentResponse.agent.slackUserId;
                }
                baseAgentInfo.assignedBrands = agentResponse.agent.brands || [];
              }
            } catch (agentErr) {
              console.log('[CallCentre] Agent lookup optional:', agentErr.message);
            }

            // Always set agent info (with or without brands)
            baseAgentInfo.brands = allBrands;
            setAgentInfo(baseAgentInfo);
            console.log('[CallCentre] Set agentInfo with', allBrands.length, 'brands');

            // Select brand from URL param or default to first brand
            if (allBrands.length > 0) {
              const brandIdFromUrl = searchParams.get('brandId');
              let brandToSelect = allBrands[0];
              if (brandIdFromUrl) {
                const foundBrand = allBrands.find(b => b.id === parseInt(brandIdFromUrl));
                if (foundBrand) {
                  brandToSelect = foundBrand;
                }
              }
              setSelectedBrand(brandToSelect);
            }

            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.error('JWT parse error:', err);
      }
    }

    // Legacy: Check for slackUserId in URL or localStorage
    const params = new URLSearchParams(window.location.search);
    const urlSlackId = params.get('slackUserId');
    const storedSlackId = localStorage.getItem('callCentre_slackUserId');

    if (urlSlackId || storedSlackId) {
      const sid = urlSlackId || storedSlackId;
      setSlackUserId(sid);
      if (urlSlackId) {
        localStorage.setItem('callCentre_slackUserId', urlSlackId);
      }
      // Load agent data using legacy method
      await loadAgentDataLegacy(sid);
    } else {
      // No session, redirect to login
      navigate('/call-centre/login');
    }

    setLoading(false);
  };

  // Legacy method for direct Slack user ID access
  const loadAgentDataLegacy = async (sid) => {
    try {
      const response = await callCentreService.getAgentInfo(sid);
      setAgentInfo(response.agent);

      if (response.agent.brands && response.agent.brands.length > 0) {
        setSelectedBrand(response.agent.brands[0]);
      }
    } catch (err) {
      console.error('Error loading agent data:', err);
      setError('Failed to load agent data. Please check your Slack User ID.');
    }
  };

  // Load data for selected brand and handle URL params
  useEffect(() => {
    if (selectedBrand) {
      loadBrandData();

      // Update URL with current brand
      updateUrlParams({ brandId: selectedBrand.id });

      // Check for search params in URL (for shared links)
      const searchFromUrl = searchParams.get('search');
      const searchTypeFromUrl = searchParams.get('searchType');
      const tabFromUrl = searchParams.get('tab');

      // Set tab from URL if present
      if (tabFromUrl !== null) {
        const tabIndex = parseInt(tabFromUrl);
        if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
          setMainTab(tabIndex);
        }
      }

      // Auto-search if search param in URL
      if (searchFromUrl && !customer) {
        const type = searchTypeFromUrl || 'phone';
        console.log('[CallCentre] Auto-searching from URL:', searchFromUrl, 'type:', type);
        setCustomerSearch(searchFromUrl);
        setSearchType(type);
        setTimeout(() => {
          handleCustomerSearch(searchFromUrl);
        }, 500);
      }
    }
  }, [selectedBrand]);

  // Auto-refresh call list every 30 seconds
  useEffect(() => {
    if (!selectedBrand) return;
    const interval = setInterval(() => {
      loadBrandData();
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedBrand]);

  const loadBrandData = async () => {
    if (!selectedBrand) return;

    try {
      // Load calls, stats, voicemails, callbacks, pending calls in parallel
      // Use allSettled so one failure doesn't block the rest
      const [callsRes, statsRes, voicemailsRes, callbacksRes, pendingRes, phoneRes, onlineRes] = await Promise.allSettled([
        callCentreService.getCalls({ brandId: selectedBrand.id, limit: 20 }),
        callCentreService.getCallStats({ brandId: selectedBrand.id }),
        callCentreService.getVoicemails({ brandId: selectedBrand.id, status: 'new', limit: 10 }),
        callCentreService.getCallbacks({ brandId: selectedBrand.id, status: 'pending', limit: 10 }),
        callCentreService.getPendingCalls({ brandId: selectedBrand.id, limit: 30 }),
        callCentreService.getPhoneNumbers(selectedBrand.id),
        callCentreService.getOnlineAgents(selectedBrand.id)
      ]);

      if (callsRes.status === 'fulfilled') setCalls(callsRes.value.calls || []);
      if (statsRes.status === 'fulfilled') setCallStats(statsRes.value.stats || null);
      if (voicemailsRes.status === 'fulfilled') setVoicemails(voicemailsRes.value.voicemails || []);
      if (callbacksRes.status === 'fulfilled') setCallbacks(callbacksRes.value.callbacks || []);
      if (pendingRes.status === 'fulfilled') setPendingCalls(pendingRes.value.missedCalls || []);
      if (phoneRes.status === 'fulfilled') setBrandPhoneNumbers(phoneRes.value.phoneNumbers || []);
      if (onlineRes.status === 'fulfilled') setOnlineAgents(onlineRes.value.agents || []);
    } catch (err) {
      console.error('Error loading brand data:', err);
    }
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('agentToken');
    localStorage.removeItem('agentInfo');
    localStorage.removeItem('callCentre_slackUserId');
    navigate('/call-centre/login');
  };

  // Load all calls for dialog with pagination
  const loadAllCalls = async (options = {}) => {
    if (!selectedBrand) return;

    const {
      filter = allCallsFilter,
      agent = allCallsAgentFilter,
      phone = allCallsPhoneSearch,
      page = allCallsPage,
      limit = allCallsLimit
    } = options;

    setAllCallsLoading(true);
    try {
      const params = {
        brandId: selectedBrand.id,
        page,
        limit
      };

      // Apply status filter
      if (filter === 'completed') {
        params.status = 'completed';
      } else if (filter === 'missed') {
        params.status = 'no-answer';
      }

      // Apply agent filter
      if (agent) {
        params.agentSlackUserId = agent;
      }

      // Apply phone search
      if (phone) {
        params.phone = phone;
      }

      const response = await callCentreService.getCalls(params);
      setAllCalls(response.calls || []);
      setAllCallsTotal(response.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading all calls:', err);
    } finally {
      setAllCallsLoading(false);
    }
  };

  // Load agents list for filter dropdown
  const loadAgentsList = async () => {
    if (!selectedBrand) return;
    try {
      // Get unique agents from recent calls
      const response = await callCentreService.getCalls({
        brandId: selectedBrand.id,
        limit: 500 // Get more to find unique agents
      });
      const uniqueAgents = new Map();
      (response.calls || []).forEach(call => {
        if (call.agent_slack_user_id && call.agent_name) {
          uniqueAgents.set(call.agent_slack_user_id, call.agent_name);
        }
      });
      setAgentsList(Array.from(uniqueAgents, ([id, name]) => ({ id, name })));
    } catch (err) {
      console.error('Error loading agents:', err);
    }
  };

  // Open all calls dialog with a specific filter
  const openAllCallsDialog = (filter = 'all') => {
    setAllCallsFilter(filter);
    setAllCallsAgentFilter('');
    setAllCallsPhoneSearch('');
    setAllCallsPage(1);
    setAllCallsExpandedId(null);
    setAllCallsDialogOpen(true);
    loadAllCalls({ filter, agent: '', phone: '', page: 1 });
    loadAgentsList();
  };

  // Handle filter change in all calls dialog
  const handleAllCallsFilterChange = (newFilter) => {
    setAllCallsFilter(newFilter);
    setAllCallsPage(1);
    loadAllCalls({ filter: newFilter, page: 1 });
  };

  // Handle agent filter change
  const handleAllCallsAgentChange = (agentId) => {
    setAllCallsAgentFilter(agentId);
    setAllCallsPage(1);
    loadAllCalls({ agent: agentId, page: 1 });
  };

  // Handle phone search with debounce
  const handleAllCallsPhoneSearch = (value) => {
    setAllCallsPhoneSearch(value);
    if (phoneSearchTimerRef.current) clearTimeout(phoneSearchTimerRef.current);
    phoneSearchTimerRef.current = setTimeout(() => {
      setAllCallsPage(1);
      loadAllCalls({ phone: value, page: 1 });
    }, 400);
  };

  // Handle page change in all calls dialog
  const handleAllCallsPageChange = (newPage) => {
    setAllCallsPage(newPage);
    loadAllCalls({ page: newPage });
  };

  // Handle page size change
  const handleAllCallsLimitChange = (newLimit) => {
    setAllCallsLimit(newLimit);
    setAllCallsPage(1);
    loadAllCalls({ limit: newLimit, page: 1 });
  };

  // Sort calls client-side (since backend doesn't support custom sort)
  const sortedAllCalls = [...allCalls].sort((a, b) => {
    let aVal = a[allCallsSortField];
    let bVal = b[allCallsSortField];

    // Handle dates
    if (allCallsSortField === 'started_at') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    // Handle numbers
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return allCallsSortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    // Handle strings
    aVal = String(aVal || '').toLowerCase();
    bVal = String(bVal || '').toLowerCase();
    if (allCallsSortDir === 'asc') {
      return aVal.localeCompare(bVal);
    }
    return bVal.localeCompare(aVal);
  });

  // Toggle sort
  const handleSortChange = (field) => {
    if (allCallsSortField === field) {
      setAllCallsSortDir(allCallsSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setAllCallsSortField(field);
      setAllCallsSortDir('desc');
    }
  };

  // Load call details (notes + tags) when expanding a row
  const loadCallDetails = async (callId) => {
    try {
      const data = await callCentreService.getCall(callId);
      if (data.success) {
        setCallNotes(prev => ({ ...prev, [callId]: data.notes || [] }));
        setCallTags(prev => ({ ...prev, [callId]: data.tags || [] }));
      }
    } catch (err) {
      console.error('[Dashboard] Error loading call details:', err);
    }
  };

  // Load popular tags for autocomplete
  const loadPopularTags = async (query = '') => {
    try {
      const data = await callCentreService.getPopularTags(selectedBrand?.id, query);
      if (data.success) {
        setPopularTags(data.tags.map(t => t.tag));
      }
    } catch (err) {
      console.error('[Dashboard] Error loading popular tags:', err);
    }
  };

  // Add tag to a call
  const handleAddTag = async (callId, tagText) => {
    if (!tagText || !tagText.trim()) return;
    const normalized = tagText.trim().toLowerCase();
    // Local dedup check — prevent double-add from UI race conditions
    const existingTags = callTags[callId] || [];
    if (existingTags.some(t => t.tag === normalized)) {
      setNewTagInput('');
      return;
    }
    setAddingTag(true);
    try {
      const agentName = agentInfo?.name || agentInfo?.real_name || '';
      const data = await callCentreService.addCallTag(callId, tagText, slackUserId, agentName);
      if (data.success) {
        const newTag = { id: data.tagId, tag: data.tag, added_by_name: agentName, added_at: new Date().toISOString() };
        // Double-check local state hasn't been updated by another call
        setCallTags(prev => {
          const current = prev[callId] || [];
          if (current.some(t => t.tag === data.tag)) return prev;
          return { ...prev, [callId]: [...current, newTag] };
        });
        updateCallInlineTag(callId, data.tag, 'add');
        setNewTagInput('');
      }
    } catch (err) {
      console.error('[Dashboard] Error adding tag:', err);
    }
    setAddingTag(false);
  };

  // Remove tag from a call
  const handleRemoveTag = async (callId, tagId, tagText) => {
    try {
      const data = await callCentreService.removeCallTag(callId, tagId);
      if (data.success) {
        setCallTags(prev => ({
          ...prev,
          [callId]: (prev[callId] || []).filter(t => t.id !== tagId)
        }));
        updateCallInlineTag(callId, tagText, 'remove');
      }
    } catch (err) {
      console.error('[Dashboard] Error removing tag:', err);
    }
  };

  // Update tags array on the call objects in both calls and allCalls
  const updateCallInlineTag = (callId, tag, action) => {
    const updater = (list) => list.map(c => {
      if (c.call_id !== callId) return c;
      const currentTags = Array.isArray(c.tags) ? [...c.tags] : [];
      if (action === 'add' && !currentTags.includes(tag)) {
        return { ...c, tags: [...currentTags, tag] };
      } else if (action === 'remove') {
        return { ...c, tags: currentTags.filter(t => t !== tag) };
      }
      return c;
    });
    setCalls(prev => updater(prev));
    setAllCalls(prev => updater(prev));
  };

  // Add note to a call
  const handleAddNote = async (callId) => {
    if (!newNoteText.trim()) return;
    setAddingNote(true);
    try {
      const data = await callCentreService.addCallNote(callId, newNoteText);
      if (data.success) {
        const agentName = agentInfo?.name || agentInfo?.real_name || 'You';
        const newNote = {
          id: data.noteId,
          note_text: newNoteText,
          note_type: 'general',
          agent_name: agentName,
          created_at: new Date().toISOString()
        };
        setCallNotes(prev => ({ ...prev, [callId]: [...(prev[callId] || []), newNote] }));
        setNewNoteText('');
      }
    } catch (err) {
      console.error('[Dashboard] Error adding note:', err);
    }
    setAddingNote(false);
  };

  // Delete note from a call
  const handleDeleteNote = async (callId, noteId) => {
    try {
      const data = await callCentreService.deleteCallNote(callId, noteId);
      if (data.success) {
        setCallNotes(prev => ({
          ...prev,
          [callId]: (prev[callId] || []).filter(n => n.id !== noteId)
        }));
      }
    } catch (err) {
      console.error('[Dashboard] Error deleting note:', err);
    }
  };

  // Handle expand toggle for Recent Calls
  const handleExpandCall = (callSid, callId, isExpanded) => {
    if (isExpanded) {
      setExpandedCallId(null);
    } else {
      setExpandedCallId(callSid);
      setNewNoteText('');
      setNewTagInput('');
      loadCallDetails(callId);
      loadPopularTags();
    }
  };

  // Handle expand toggle for All Calls dialog
  const handleExpandAllCall = (callSid, callId, isExpanded) => {
    if (isExpanded) {
      setAllCallsExpandedId(null);
    } else {
      setAllCallsExpandedId(callSid);
      setNewNoteText('');
      setNewTagInput('');
      loadCallDetails(callId);
      loadPopularTags();
    }
  };

  // Customer search - can be called with direct phone number (for incoming calls)
  const handleCustomerSearch = async (directPhone = null) => {
    const searchValue = directPhone || customerSearch;
    const type = directPhone ? 'phone' : searchType;

    if (!searchValue.trim() || !selectedBrand) return;

    setIsSearching(true);
    setCustomer(null);
    setCustomers([]);
    setCustomerPreview(null);
    setCustomerOrders([]);
    setCustomerCalls([]);
    setCustomerEdeskTickets([]);

    // Update the search field if using direct phone
    if (directPhone) {
      setCustomerSearch(directPhone);
      setSearchType('phone');
    }

    // Update URL with search params
    updateUrlParams({ search: searchValue, searchType: type });

    try {
      let response;

      switch (type) {
        case 'phone':
          response = await callCentreService.lookupCustomer(searchValue, selectedBrand.id);
          break;
        case 'email':
          response = await callCentreService.searchByEmail(searchValue, selectedBrand.id);
          break;
        case 'name':
          response = await callCentreService.searchByName(searchValue, selectedBrand.id);
          break;
        case 'order':
          response = await callCentreService.searchByOrder(searchValue, selectedBrand.id);
          break;
        default:
          response = await callCentreService.lookupCustomer(searchValue, selectedBrand.id);
      }

      if (response.found && response.customer) {
        // If multiple customers returned (name search), store them all
        if (response.customers && response.customers.length > 1) {
          setCustomers(response.customers);
          // Show first customer by default
          const first = response.customers[0];
          setCustomer(first);
          setCustomerOrders(first.orders || []);
        } else {
          setCustomers([]);
          setCustomer(response.customer);
          setCustomerOrders(response.allOrders || response.orders || []);
        }
        setCustomerCalls(response.previousCalls || []);
        setCustomerEdeskTickets(response.edeskTickets || []);
      } else {
        setCustomer({ notFound: true, searchTerm: searchValue });
      }
    } catch (err) {
      console.error('Error searching customer:', err);
      setError('Failed to search customer');
    } finally {
      setIsSearching(false);
    }
  };

  // Product search
  const handleProductSearch = async () => {
    if (!productSearch.trim() || !selectedBrand) return;

    setIsSearchingProducts(true);
    try {
      const response = await callCentreService.searchProducts(selectedBrand.id, productSearch);
      setProducts(response.products || []);
    } catch (err) {
      console.error('Error searching products:', err);
    } finally {
      setIsSearchingProducts(false);
    }
  };

  // Cart management
  const addToCart = (product, variant) => {
    const existingIndex = cart.findIndex(item => item.variantId === variant.id);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        variantId: variant.id,
        productTitle: product.title,
        variantTitle: variant.title,
        price: variant.price,
        sku: variant.sku,
        quantity: 1
      }]);
    }
  };

  const updateCartQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setCart(newCart);
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Create draft order
  const handleCreateOrder = async () => {
    if (cart.length === 0 || !customer || customer.notFound) return;

    setSendingOrder(true);
    try {
      const orderData = {
        brandId: selectedBrand.id,
        customerPhone: customer.phone || customerSearch,
        customerEmail: customer.email || '',
        customerName: customer.name || '',
        lineItems: cart.map(item => ({
          variantId: item.variantId,
          quantity: item.quantity
        })),
        note: `Phone order created by agent`,
        // Discount fields
        discountCode: discountCode || undefined,
        discountType: discountValue ? discountType : undefined,
        discountValue: discountValue || undefined,
        // Auto-fill addresses from customer's previous order
        shippingAddress: customer.shippingAddress || undefined,
        billingAddress: customer.billingAddress || undefined
      };

      const response = await callCentreService.createDraftOrder(orderData);
      setDraftOrder(response.draftOrder);
      setError(null); setOrderDialogOpen(true);
      // Reset discount fields after order created
      setDiscountCode('');
      setDiscountValue('');
    } catch (err) {
      console.error('Error creating draft order:', err);
      setError('Failed to create order');
    } finally {
      setSendingOrder(false);
    }
  };

  // Send order via SMS
  const handleSendSms = async () => {
    if (!draftOrder) return;

    setSendingOrder(true);
    try {
      await callCentreService.sendDraftOrderSms(draftOrder.crmId);
      setOrderDialogOpen(false);
      setCart([]);
      setDraftOrder(null);
      alert('Payment link sent via SMS!');
    } catch (err) {
      console.error('Error sending SMS:', err);
      setError('Failed to send SMS');
    } finally {
      setSendingOrder(false);
    }
  };

  // Format phone for display
  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    return phone;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '1970-01-01 00:00:00') return 'N/A';
    // ClickHouse returns UTC timestamps without timezone suffix (e.g. "2026-03-18 19:02:28")
    // Append 'Z' so JavaScript correctly treats it as UTC and converts to user's local timezone
    let str = dateStr;
    if (!/[Z+\-]\d{0,4}:?\d{0,2}$/.test(str) && !str.endsWith('Z')) {
      str = str.replace(' ', 'T') + 'Z';
    }
    return new Date(str).toLocaleString();
  };

  // Format currency
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Play a call's recording. Asks the backend for the right URL — when
  // RECORDINGS_PROXY_ENABLED is true the backend returns a short-lived proxied
  // URL (so the agent's browser never talks to api.twilio.com directly); when
  // disabled it returns the original Twilio URL, making the flag transparent.
  // The third arg to toggleAudio is the identity key — we always pass the
  // original recording_url so the existing playingAudio === call.recording_url
  // comparisons in the JSX continue to work regardless of which URL is fetched.
  // Falls back to the direct recording_url on any backend failure so a bad
  // deploy can never break playback.
  const playRecording = async (call, knownDuration) => {
    if (!call) return;
    const identity = call.recording_url || call.recordingUrl || '';
    let url = identity;
    if (call.id) {
      try {
        const data = await callCentreService.getPlaybackUrl(call.id);
        if (data && data.url) url = data.url;
      } catch (err) {
        console.warn('[recording] getPlaybackUrl failed, falling back to direct URL:', err?.message);
      }
    }
    if (!url) return;
    toggleAudio(url, knownDuration, identity);
  };

  // Play/pause audio recording inline.
  // identityKey lets the caller decouple the URL used for fetch (`url`) from
  // the value stored in `playingAudio` for play/pause comparisons. When omitted
  // we keep the original behaviour and use `url` itself.
  const toggleAudio = (url, knownDuration, identityKey = null) => {
    const stateKey = identityKey || url;
    if (playingAudio === stateKey) {
      // Same audio - pause it
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setPlayingAudio(null);
    } else {
      // Different audio - stop current and play new
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      // Set known duration immediately if available (Twilio streams don't report duration)
      if (knownDuration && knownDuration > 0) {
        setAudioDuration(knownDuration);
      } else {
        setAudioDuration(0);
      }
      setAudioProgress(0);
      const audio = new Audio(url);
      audio.playbackRate = playbackSpeed;
      audio.onloadedmetadata = () => {
        if (audio.duration && isFinite(audio.duration)) setAudioDuration(audio.duration);
      };
      audio.ondurationchange = () => {
        if (audio.duration && isFinite(audio.duration)) setAudioDuration(audio.duration);
      };
      audio.onended = () => {
        setPlayingAudio(null);
        setAudioProgress(0);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
      audio.onerror = () => {
        setPlayingAudio(null);
        setAudioProgress(0);
        setError('Failed to play audio');
      };
      audioRef.current = audio;
      audio.play().catch(err => {
        // Benign HTML5 media race: play() promise rejects with AbortError when
        // pause()/source-switch happens before it resolves (goo.gl/LdLk22). Ignore it;
        // surface anything genuinely broken (autoplay blocked, network, decode).
        if (err && err.name !== 'AbortError') {
          console.error('[CallCentre] Audio play failed:', err);
          setError('Failed to play audio');
        }
      });
      setPlayingAudio(stateKey);
      // Update progress every 100ms
      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setAudioProgress(audioRef.current.currentTime);
          // Keep trying to get duration from browser if not yet available
          if (audioRef.current.duration && isFinite(audioRef.current.duration)) {
            setAudioDuration(audioRef.current.duration);
          }
        }
      }, 100);
    }
  };

  // Skip audio forward/backward
  const skipAudio = (seconds) => {
    if (audioRef.current && playingAudio) {
      const newTime = Math.max(0, Math.min(audioRef.current.duration, audioRef.current.currentTime + seconds));
      audioRef.current.currentTime = newTime;
      setAudioProgress(newTime);
    }
  };

  // Change playback speed
  const changePlaybackSpeed = () => {
    const speeds = [1, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  // Format audio time as MM:SS
  const formatAudioTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state (no agent info)
  if (error && !agentInfo) {
    return (
      <Box sx={{ p: 4, maxWidth: 500, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/call-centre/login')}>
          Sign In with Slack
        </Button>
      </Box>
    );
  }

  // Not authenticated
  if (!agentInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Format duration for display
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f5f7fa',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      position: 'relative'
    }}>
      {/* Top Header Bar */}
      <Box sx={{
        bgcolor: 'white',
        borderBottom: '1px solid #e0e0e0',
        px: 2,
        py: 1.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PhoneIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
              Call Centre
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {agentInfo?.name || agentInfo?.email}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Brand Selector */}
          {agentInfo?.brands && agentInfo.brands.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={selectedBrand?.id || ''}
                onChange={(e) => {
                  const brand = agentInfo.brands.find(b => b.id === e.target.value);
                  setSelectedBrand(brand);
                  // Clear search when brand changes and update URL
                  setCustomer(null);
                  setCustomers([]);
                  setCustomerPreview(null);
                  setCustomerOrders([]);
                  setCustomerEdeskTickets([]);
                  setCustomerSearch('');
                  updateUrlParams({ brandId: brand?.id, search: null, searchType: null });
                }}
                displayEmpty
                sx={{ bgcolor: '#f5f7fa', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' } }}
              >
                {agentInfo.brands.map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Tooltip title="Refresh">
            <IconButton onClick={loadBrandData} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {pushSupported && (
            <Tooltip title={pushEnabled ? "Notifications enabled" : "Enable notifications"}>
              <IconButton
                onClick={() => {
                  console.log('[Push] Bell clicked!');
                  enablePushNotifications();
                }}
                color={pushEnabled ? "primary" : "default"}
                size="small"
              >
                <NotificationsIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Sign Out">
            <IconButton onClick={handleLogout} size="small">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Stats Bar - Clickable */}
      {callStats && (
        <Box sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: 2,
          py: 1,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <Box
            onClick={() => openAllCallsDialog('all')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              p: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: '#f5f7fa' }
            }}
          >
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'success.main'
            }} />
            <Typography variant="body2" color="text.secondary">Total Calls</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{callStats.total_calls || 0}</Typography>
          </Box>
          <Box
            onClick={() => openAllCallsDialog('completed')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              p: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: '#f5f7fa' }
            }}
          >
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.main'
            }} />
            <Typography variant="body2" color="text.secondary">Completed</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>{callStats.completed_calls || 0}</Typography>
          </Box>
          <Box
            onClick={() => openAllCallsDialog('missed')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              p: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: '#f5f7fa' }
            }}
          >
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'error.main'
            }} />
            <Typography variant="body2" color="text.secondary">Missed</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>{callStats.missed_calls || 0}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'warning.main'
            }} />
            <Typography variant="body2" color="text.secondary">Voicemails</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>{voicemails.length}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'info.main'
            }} />
            <Typography variant="body2" color="text.secondary">Callbacks</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>{callbacks.length}</Typography>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ ml: 'auto', alignItems: 'center' }}>
            {/* Online agents for selected brand */}
            {onlineAgents.length > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Tooltip title={onlineAgents.map(a => `${a.name} (${a.devices.map(d => d.type).join(', ')})`).join('\n')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.7rem' }}>
                      {onlineAgents.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      {onlineAgents.map(a => (a.name || '').split(' ')[0]).join(', ')}
                    </Typography>
                  </Box>
                </Tooltip>
              </>
            )}
            {onlineAgents.length === 0 && selectedBrand && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                    No agents online
                  </Typography>
                </Box>
              </>
            )}
            {/* Brand phone numbers */}
            {brandPhoneNumbers.filter(p => p.is_active).length > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                {brandPhoneNumbers.filter(p => p.is_active).map((phone) => (
                  <Box key={phone.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', bgcolor: '#f0f0f0', px: 0.5, borderRadius: 0.5 }}>
                      {phone.country_code}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {phone.phone_number}
                    </Typography>
                  </Box>
                ))}
              </>
            )}
          </Stack>
        </Box>
      )}

      {/* Incoming Call Banner - Shows prominently when call is ringing */}
      {incomingCallInfo && (
        <Box sx={{
          mx: 2,
          mt: 2,
          p: 2,
          background: 'var(--gradient-primary)',
          borderRadius: 2,
          color: 'white',
          animation: 'pulse 1.5s infinite',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(var(--color-primary-rgb), 0.7)' },
            '70%': { boxShadow: '0 0 0 15px rgba(var(--color-primary-rgb), 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(var(--color-primary-rgb), 0)' }
          }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            {/* Left: Call Info */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PhoneIcon sx={{ animation: 'ring 0.5s infinite' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  INCOMING CALL
                </Typography>
                {incomingCallInfo.brandName && (
                  <Chip
                    label={incomingCallInfo.brandName}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                )}
                {incomingCallInfo.countryCode && (
                  <Chip
                    label={incomingCallInfo.countryCode}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 700 }}
                  />
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {incomingCallInfo.from}
              </Typography>
              {incomingCallInfo.callerName && (
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {incomingCallInfo.callerName}
                </Typography>
              )}
            </Box>

            {/* Right: Customer Info */}
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2,
              p: 1.5,
              minWidth: 250
            }}>
              {incomingCallInfo.loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  <Typography variant="body2">Loading customer info...</Typography>
                </Box>
              ) : incomingCallInfo.customer ? (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {incomingCallInfo.customer.name || 'Customer'}
                  </Typography>
                  {incomingCallInfo.customer.email && (
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                      {incomingCallInfo.customer.email}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {incomingCallInfo.customer.orderCount}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>Orders</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {incomingCallInfo.customer.totalSpent}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>Lifetime Value</Typography>
                    </Box>
                  </Box>
                  {incomingCallInfo.customer.lastOrderDate && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                      Last order: {new Date(incomingCallInfo.customer.lastOrderDate).toLocaleDateString()}
                    </Typography>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="h6">🆕</Typography>
                  <Typography variant="body2">New Customer</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ p: 2, maxWidth: '100%', overflow: 'hidden' }}>
        <Box sx={{
          display: { xs: 'block', md: 'grid' },
          gridTemplateColumns: { md: '1fr 1fr 1fr' },
          gap: 2
        }}>
          {/* Left Column - Softphone & Customer */}
          <Box sx={{ minWidth: 0 }}>
            {/* Softphone */}
            {slackUserId && (
              <Paper sx={{ mb: 2, overflow: 'hidden' }} elevation={0}>
                <Softphone
                  ref={softphoneRef}
                  slackUserId={slackUserId}
                  selectedBrandId={selectedBrand?.id}
                  brandPhoneNumbers={brandPhoneNumbers}
                  onCallStart={(callInfo) => {
                    console.log('Call started:', callInfo);
                    setIncomingCallInfo(null); // Dismiss incoming call banner when answered
                    loadBrandData();
                  }}
                  onCallEnd={async () => {
                    console.log('Call ended');
                    setIncomingCallInfo(null);
                    if (activeCallbackId) {
                      try {
                        await callCentreService.completeCallback(activeCallbackId);
                        console.log('[Dashboard] Callback', activeCallbackId, 'marked complete');
                      } catch (err) {
                        console.error('[Dashboard] Error completing callback:', err);
                      }
                      setActiveCallbackId(null);
                    }
                    // Immediate refresh + delayed refresh (webhook may arrive after SDK disconnect)
                    loadBrandData();
                    setTimeout(() => loadBrandData(), 3000);
                  }}
                  onIncomingCall={async (callInfo) => {
                    console.log('Incoming call:', callInfo);

                    // Find country code from the Twilio number the call came in on
                    const matchedPhone = callInfo.toNumber ? brandPhoneNumbers.find(p => p.phone_number === callInfo.toNumber) : null;

                    // Set incoming call banner immediately
                    setIncomingCallInfo({
                      from: callInfo.from,
                      callerName: callInfo.callerName,
                      brandId: callInfo.brandId,
                      brandName: callInfo.brandName,
                      toNumber: callInfo.toNumber,
                      countryCode: matchedPhone?.country_code || null,
                      loading: true,
                      customer: null
                    });

                    // Auto-select brand if provided and different from current
                    let brandForLookup = selectedBrand;
                    if (callInfo.brandId && agentInfo?.brands) {
                      const incomingBrand = agentInfo.brands.find(b => b.id === callInfo.brandId);
                      if (incomingBrand) {
                        console.log('Auto-selecting brand for incoming call:', incomingBrand.name);
                        setSelectedBrand(incomingBrand);
                        brandForLookup = incomingBrand;
                      }
                    }

                    // Look up customer details for the banner
                    if (callInfo.brandId && callInfo.from) {
                      try {
                        const response = await callCentreService.lookupCustomer(callInfo.from, callInfo.brandId);
                        if (response.success && response.found) {
                          const orders = response.orders || [];
                          const lastOrderDate = orders.length > 0 ? orders[0].order_date : null;
                          setIncomingCallInfo(prev => ({
                            ...prev,
                            loading: false,
                            customer: {
                              name: response.customer?.name,
                              email: response.customer?.email,
                              // lifetime covers all their orders; orders is only the latest 10 (DT-1099)
                              orderCount: response.lifetime ? response.lifetime.orderCount : orders.length,
                              totalSpent: totalsByCurrency(response.lifetime ? response.lifetime.totals : orders),
                              lastOrderDate
                            }
                          }));
                        } else {
                          setIncomingCallInfo(prev => ({ ...prev, loading: false }));
                        }
                      } catch (e) {
                        console.log('Could not lookup customer for banner:', e);
                        setIncomingCallInfo(prev => ({ ...prev, loading: false }));
                      }
                    } else {
                      setIncomingCallInfo(prev => ({ ...prev, loading: false }));
                    }

                    // Also trigger the customer search in the main panel
                    if (callInfo.from && brandForLookup) {
                      setTimeout(() => handleCustomerSearch(callInfo.from), 100);
                    } else if (callInfo.from) {
                      setCustomerSearch(callInfo.from);
                      setSearchType('phone');
                    }
                  }}
                />
              </Paper>
            )}

            {/* Customer Search */}
            <Paper sx={{ p: 2.5, mb: 2 }} elevation={0}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon fontSize="small" />
                Customer Lookup
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                {['phone', 'email', 'name', 'order'].map((type) => (
                  <Chip
                    key={type}
                    label={type === 'phone' ? 'Phone' : type === 'email' ? 'Email' : type === 'name' ? 'Name' : 'Order #'}
                    onClick={() => setSearchType(type)}
                    color={searchType === type ? 'primary' : 'default'}
                    variant={searchType === type ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Stack>

              <TextField
                fullWidth
                size="small"
                placeholder={searchType === 'phone' ? '+1234567890' : searchType === 'email' ? 'customer@email.com' : searchType === 'name' ? 'John Smith' : 'Order #12345'}
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCustomerSearch()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCustomerSearch()} disabled={isSearching} size="small">
                        {isSearching ? <CircularProgress size={18} /> : <SearchIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { bgcolor: '#f5f7fa' }
                }}
              />
            </Paper>

          {/* Multiple Customer Results */}
          {customers.length > 1 && (
            <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {customers.length} customers found — click to view:
              </Typography>
              <Stack spacing={0.5}>
                {customers.map((c, idx) => {
                  const isActive = customer?.email === c.email && customer?.name === c.name;
                  return (
                    <Box
                      key={idx}
                      onClick={() => setCustomerPreview(c)}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        bgcolor: isActive ? 'primary.light' : '#f5f7fa',
                        color: isActive ? 'primary.contrastText' : 'text.primary',
                        '&:hover': { bgcolor: isActive ? 'primary.light' : '#e8ecf0' },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {c.name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {c.email || 'No email'}{c.phone ? ` · ${c.phone}` : ''}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${c.totalOrders} order${c.totalOrders !== 1 ? 's' : ''}`}
                        sx={{ bgcolor: isActive ? 'rgba(255,255,255,0.3)' : undefined }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          )}

          {/* Customer Preview Dialog (for multi-result name search) */}
          <Dialog
            open={!!customerPreview}
            onClose={() => setCustomerPreview(null)}
            maxWidth="sm"
            fullWidth
          >
            {customerPreview && (
              <>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                      {(customerPreview.name || 'C').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {customerPreview.name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {customerPreview.email || 'No email'}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setCustomerPreview(null)}>
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2 }}>
                  {/* Contact Info */}
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body2">{formatPhone(customerPreview.phone)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Orders</Typography>
                      <Typography variant="body2">{customerPreview.totalOrders}</Typography>
                    </Box>
                    {customerPreview.lifetimeValue > 0 && customerPreview.lifetimeValueCurrency && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">Lifetime Value</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(customerPreview.lifetimeValue, customerPreview.lifetimeValueCurrency)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  {/* Orders */}
                  {customerPreview.orders && customerPreview.orders.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HistoryIcon fontSize="small" />
                        Order History
                      </Typography>
                      <Stack spacing={1} sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {customerPreview.orders.map((order, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1.5,
                              bgcolor: '#f5f7fa',
                              borderRadius: 1,
                              borderLeft: '3px solid',
                              borderLeftColor: order.fulfillment_status === 'FULFILLED' ? 'success.main' : 'warning.main'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.order_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{formatDate(order.order_date)}</Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(order.total, order.currency)}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={order.fulfillment_status || 'Pending'}
                                  color={order.fulfillment_status === 'FULFILLED' ? 'success' : 'warning'}
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                  <Button onClick={() => setCustomerPreview(null)} size="small">
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setCustomer(customerPreview);
                      setCustomerOrders(customerPreview.orders || []);
                      setCustomerPreview(null);
                    }}
                    startIcon={<PhoneIcon />}
                  >
                    Select Customer
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Customer Info */}
          {customer && (
            <Paper sx={{ p: 2.5, mb: 2 }} elevation={0}>
              {customer.notFound ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No customer found for "{customer.searchTerm}"
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    New customer or pre-purchase inquiry
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                      {(customer.name || 'C').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {customer.name || 'Unknown Customer'}
                      </Typography>
                      {customer.totalOrders && (
                        <Chip
                          size="small"
                          label={`${customer.totalOrders} orders`}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                    {customer.phone && (
                      <Tooltip title="Call customer">
                        <IconButton
                          color="success"
                          onClick={() => handleClickToCall(customer.phone)}
                          sx={{ bgcolor: 'success.light', '&:hover': { bgcolor: 'success.main', color: 'white' } }}
                        >
                          <PhoneIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">{customer.email || 'N/A'}</Typography>
                        {customer.email && (
                          <IconButton size="small" onClick={() => copyToClipboard(customer.email)}>
                            <CopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">{formatPhone(customer.phone)}</Typography>
                        {customer.phone && (
                          <IconButton size="small" onClick={() => copyToClipboard(customer.phone)}>
                            <CopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Stack>
                </>
              )}
            </Paper>
          )}

          {/* eDesk Tickets */}
          {customerEdeskTickets.length > 0 && (
            <Paper sx={{ p: 2.5, mb: 2, maxHeight: 300, overflow: 'auto' }} elevation={0}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConfirmationNumberIcon fontSize="small" />
                eDesk Tickets ({customerEdeskTickets.length})
              </Typography>

              {customerEdeskTickets.map((ticket) => (
                <Box
                  key={ticket.ticket_id}
                  onClick={() => window.open(`https://dashboard-1.edesk.com/crm/view/${ticket.ticket_id}`, '_blank')}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    bgcolor: '#f5f7fa',
                    borderRadius: 1,
                    borderLeft: '3px solid',
                    borderLeftColor: ticket.status === 'closed' ? 'success.main' : 'warning.main',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#e8ecf0' }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {ticket.subject}
                    </Typography>
                    <Chip
                      size="small"
                      label={ticket.status}
                      color={ticket.status === 'closed' ? 'success' : 'warning'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    #{ticket.ticket_id} · {formatDate(ticket.created_at)}
                    {ticket.contact_name && ` · ${ticket.contact_name}`}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {/* Customer Orders */}
          {customerOrders.length > 0 && (
            <Paper sx={{ p: 2.5, mb: 2, maxHeight: 500, overflow: 'auto' }} elevation={0}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon fontSize="small" />
                Order History ({customerOrders.length})
              </Typography>

              {customerOrders.map((order, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    bgcolor: '#f5f7fa',
                    borderRadius: 1,
                    borderLeft: '3px solid',
                    borderLeftColor: order.fulfillment_status === 'FULFILLED' ? 'success.main' : 'warning.main'
                  }}
                >
                  {/* Order Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {order.order_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(order.order_date)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(order.total, order.currency)}
                      </Typography>
                      <Chip
                        size="small"
                        label={order.fulfillment_status || 'Pending'}
                        color={order.fulfillment_status === 'FULFILLED' ? 'success' : 'warning'}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    </Box>
                  </Box>

                  {/* Line Items */}
                  {order.lineItems && order.lineItems.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #ddd' }}>
                      {order.lineItems.slice(0, 3).map((item, itemIdx) => (
                        <Box key={itemIdx} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                          {item.imageUrl ? (
                            <Box
                              component="img"
                              src={item.imageUrl}
                              alt={item.title}
                              sx={{ width: 32, height: 32, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <Box sx={{ width: 32, height: 32, borderRadius: 0.5, bgcolor: '#e0e0e0', flexShrink: 0 }} />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }} noWrap>
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.variantTitle !== 'Default Title' && item.variantTitle} × {item.quantity}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {formatCurrency(item.totalPrice, order.currency)}
                          </Typography>
                        </Box>
                      ))}
                      {order.lineItems.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{order.lineItems.length - 3} more items
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Tracking */}
                  {order.tracking_number && (
                    <Typography
                      variant="caption"
                      component={order.tracking_url ? 'a' : 'span'}
                      href={order.tracking_url || undefined}
                      target={order.tracking_url ? '_blank' : undefined}
                      rel={order.tracking_url ? 'noopener noreferrer' : undefined}
                      sx={{
                        mt: 1,
                        display: 'block',
                        color: 'primary.main',
                        textDecoration: order.tracking_url ? 'underline' : 'none',
                        cursor: order.tracking_url ? 'pointer' : 'default',
                        '&:hover': order.tracking_url ? { color: 'primary.dark' } : {}
                      }}
                    >
                      🚚 {order.tracking_number} {order.carrier && `(${order.carrier})`}
                    </Typography>
                  )}
                </Box>
              ))}
            </Paper>
          )}
        </Box>

        {/* Middle Column - Queues */}
        <Box sx={{ minWidth: 0 }}>
          <Paper sx={{ mb: 2, overflow: 'hidden' }} elevation={0}>
            <Tabs
              value={mainTab}
              onChange={(e, v) => { setMainTab(v); updateUrlParams({ tab: v }); }}
              variant="fullWidth"
              sx={{
                borderBottom: '1px solid #e0e0e0',
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 }
              }}
            >
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Recent
                  {calls.length > 0 && <Chip size="small" label={calls.length} sx={{ height: 20 }} />}
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Voicemails
                  {voicemails.length > 0 && <Chip size="small" label={voicemails.length} color="error" sx={{ height: 20 }} />}
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Callbacks
                  {callbacks.length > 0 && <Chip size="small" label={callbacks.length} color="warning" sx={{ height: 20 }} />}
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Pending
                  {pendingCalls.length > 0 && <Chip size="small" label={pendingCalls.length} color="info" sx={{ height: 20 }} />}
                </Box>
              } />
            </Tabs>

            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {/* Recent Calls Tab */}
              <TabPanel value={mainTab} index={0}>
                {calls.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PhoneIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No recent calls</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {calls.map((call) => {
                      // For outbound calls, customer is to_number; for inbound, customer is from_number
                      const customerPhone = call.direction === 'outbound' ? call.to_number : call.from_number;
                      const isExpanded = expandedCallId === call.twilio_call_sid;
                      return (
                      <Box key={call.twilio_call_sid}>
                        <ListItem
                          button
                          onClick={() => handleCustomerSearch(customerPhone)}
                          sx={{
                            borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0',
                            '&:hover': { bgcolor: '#f5f7fa' },
                            cursor: 'pointer',
                            bgcolor: isExpanded ? '#f5f7fa' : 'transparent'
                          }}
                          secondaryAction={
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleExpandCall(call.twilio_call_sid, call.call_id, isExpanded); }}
                                sx={{ color: 'text.secondary' }}
                              >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                              {call.recording_url && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); playRecording(call, call.talk_duration); }}
                                  sx={{
                                    bgcolor: playingAudio === call.recording_url ? 'primary.main' : 'transparent',
                                    color: playingAudio === call.recording_url ? 'white' : 'primary.main',
                                    '&:hover': { bgcolor: 'primary.light' }
                                  }}
                                >
                                  {playingAudio === call.recording_url ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); const cid = call.direction === 'inbound' ? call.to_number : call.from_number; handleClickToCall(customerPhone, cid); }}
                                color="success"
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                  width: 28, height: 28, borderRadius: '50%', display: 'flex',
                                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                  bgcolor: call.direction === 'outbound' ? '#e3f2fd' : '#e8f5e9',
                                  color: call.direction === 'outbound' ? '#1565c0' : '#2e7d32'
                                }}>
                                  {call.direction === 'outbound'
                                    ? <CallMadeIcon sx={{ fontSize: 16 }} />
                                    : <CallReceivedIcon sx={{ fontSize: 16 }} />}
                                </Box>
                                {call.phone_country_code && (
                                  <Chip
                                    size="small"
                                    label={call.phone_country_code}
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                                  />
                                )}
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatPhone(customerPhone)}
                                </Typography>
                                <CallbackWindowBadge phone={customerPhone} />
                                <Chip
                                  size="small"
                                  label={call.status}
                                  color={call.status === 'completed' ? 'success' : 'error'}
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                                {call.talk_duration > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    {Math.floor(call.talk_duration / 60)}:{String(call.talk_duration % 60).padStart(2, '0')}
                                  </Typography>
                                )}
                                {call.edesk_ticket_id && (
                                  <Tooltip title={`eDesk Ticket #${call.edesk_ticket_id}`}>
                                    <ConfirmationNumberIcon
                                      sx={{ fontSize: 14, color: 'success.main', cursor: 'pointer' }}
                                      onClick={(e) => { e.stopPropagation(); window.open(`https://dashboard-1.edesk.com/crm/view/${call.edesk_ticket_id}`, '_blank'); }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(call.started_at)}
                                </Typography>
                                {call.agent_name && (
                                  <Chip
                                    size="small"
                                    label={call.agent_name.split(' ')[0]}
                                    variant="outlined"
                                    sx={{ height: 16, fontSize: '0.6rem' }}
                                  />
                                )}
                                {call.transcript && (
                                  <Chip
                                    size="small"
                                    label="📝"
                                    sx={{ height: 16, fontSize: '0.6rem' }}
                                  />
                                )}
                                {Array.isArray(call.tags) && call.tags.map((tag, i) => (
                                  <Chip
                                    key={i}
                                    size="small"
                                    icon={<TagIcon sx={{ fontSize: '0.6rem !important' }} />}
                                    label={tag}
                                    sx={{ height: 16, fontSize: '0.6rem', maxWidth: 80 }}
                                  />
                                ))}
                              </Box>
                            }
                          />
                        </ListItem>
                        {/* Expanded section for transcript and audio controls */}
                        {isExpanded && (
                          <Box sx={{ px: 2, pb: 2, bgcolor: '#f5f7fa', borderBottom: '1px solid #f0f0f0' }}>
                            {/* Audio controls if playing this recording */}
                            {call.recording_url && playingAudio === call.recording_url && (
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: 35 }}>
                                    {formatAudioTime(audioProgress)}
                                  </Typography>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      height: 4,
                                      bgcolor: 'grey.300',
                                      borderRadius: 2,
                                      cursor: 'pointer',
                                      position: 'relative'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (audioRef.current && audioDuration) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const percent = (e.clientX - rect.left) / rect.width;
                                        const newTime = percent * audioDuration;
                                        audioRef.current.currentTime = newTime;
                                        setAudioProgress(newTime);
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        height: '100%',
                                        bgcolor: 'primary.main',
                                        borderRadius: 2,
                                        width: `${audioDuration ? (audioProgress / audioDuration) * 100 : 0}%`
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ minWidth: 35 }}>
                                    {formatAudioTime(audioDuration)}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                  <Tooltip title="Back 10s">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); skipAudio(-10); }}>
                                      <Replay10Icon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Forward 10s">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); skipAudio(10); }}>
                                      <Forward10Icon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Chip
                                    size="small"
                                    label={`${playbackSpeed}x`}
                                    onClick={(e) => { e.stopPropagation(); changePlaybackSpeed(); }}
                                    sx={{ cursor: 'pointer', minWidth: 45 }}
                                  />
                                </Stack>
                              </Box>
                            )}
                            {/* Transcript */}
                            {call.transcript ? (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  Transcript
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.primary', fontSize: '0.85rem' }}>
                                  {call.transcript}
                                </Typography>
                              </Box>
                            ) : call.transcript_status === 'pending' ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Transcript processing...
                              </Typography>
                            ) : call.recording_url ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No transcript available
                              </Typography>
                            ) : null}
                            {/* Tags Section */}
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Tags
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                {(callTags[call.call_id] || []).map((t) => (
                                  <Chip
                                    key={t.id}
                                    size="small"
                                    label={t.tag}
                                    onDelete={() => handleRemoveTag(call.call_id, t.id, t.tag)}
                                    sx={{ height: 22, fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={popularTags}
                                inputValue={newTagInput}
                                onInputChange={(e, val) => {
                                  setNewTagInput(val);
                                  if (val.length >= 1) loadPopularTags(val);
                                }}
                                onChange={(e, val) => {
                                  if (val) { handleAddTag(call.call_id, val); setNewTagInput(''); }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Add tag..."
                                    variant="outlined"
                                    size="small"
                                    sx={{ maxWidth: 200 }}
                                  />
                                )}
                                disabled={addingTag}
                              />
                            </Box>
                            {/* Notes Section */}
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Notes
                              </Typography>
                              {(callNotes[call.call_id] || []).map((note) => (
                                <Box key={note.id} sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{note.note_text}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {note.agent_name || 'Agent'} &middot; {formatDate(note.created_at)}
                                    </Typography>
                                  </Box>
                                  <IconButton size="small" onClick={() => handleDeleteNote(call.call_id, note.id)} sx={{ mt: -0.5, opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              ))}
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <TextField
                                  size="small"
                                  placeholder="Add a note..."
                                  value={newNoteText}
                                  onChange={(e) => setNewNoteText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleAddNote(call.call_id);
                                    }
                                  }}
                                  sx={{ flex: 1 }}
                                  multiline
                                  maxRows={3}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleAddNote(call.call_id)}
                                  disabled={addingNote || !newNoteText.trim()}
                                  sx={{ minWidth: 'auto', px: 2 }}
                                >
                                  {addingNote ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );})}
                  </List>
                )}
              </TabPanel>

              {/* Voicemails Tab */}
              <TabPanel value={mainTab} index={1}>
                {voicemails.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <VoicemailIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No voicemails</Typography>
                  </Box>
                ) : (
                  <Box>
                    {voicemails.map((vm) => (
                      <Box
                        key={vm.id}
                        sx={{
                          p: 1.5,
                          borderBottom: '1px solid #f0f0f0',
                          bgcolor: playingAudio === vm.recording_url ? 'primary.lighter' : 'transparent',
                          '&:hover': { bgcolor: playingAudio === vm.recording_url ? 'primary.lighter' : '#f5f7fa' }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box
                            sx={{ flex: 1, cursor: 'pointer' }}
                            onClick={() => handleCustomerSearch(vm.from_number)}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatPhone(vm.from_number)}
                              </Typography>
                              <CallbackWindowBadge phone={vm.from_number} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(vm.created_at)}
                            </Typography>
                            {vm.transcript && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.4 }}>
                                "{vm.transcript}"
                              </Typography>
                            )}
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            {vm.recording_url && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  playRecording(vm, vm.duration);
                                  if (vm.status === 'new' && playingAudio !== vm.recording_url) {
                                    callCentreService.markVoicemailListened(vm.id).catch(err => console.error('[Dashboard] Error marking voicemail listened:', err));
                                  }
                                }}
                                sx={{
                                  bgcolor: playingAudio === vm.recording_url ? 'primary.main' : 'transparent',
                                  color: playingAudio === vm.recording_url ? 'white' : 'default'
                                }}
                              >
                                {playingAudio === vm.recording_url ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                              </IconButton>
                            )}
                            <IconButton size="small" color="success" onClick={(e) => {
                              e.stopPropagation();
                              callCentreService.actionVoicemail(vm.id, { actionNotes: 'Called back' }).catch(err => console.error('[Dashboard] Error actioning voicemail:', err));
                              setVoicemails(prev => prev.filter(v => v.id !== vm.id));
                              handleClickToCall(vm.from_number, vm.to_number);
                            }}>
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                            <Tooltip title="Dismiss">
                              <IconButton size="small" color="default" onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await callCentreService.actionVoicemail(vm.id, { actionNotes: 'Dismissed' });
                                  setVoicemails(prev => prev.filter(v => v.id !== vm.id));
                                } catch (err) {
                                  console.error('[Dashboard] Error dismissing voicemail:', err);
                                }
                              }}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        {/* Audio Controls - Show when playing */}
                        {playingAudio === vm.recording_url && (
                          <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #ddd' }}>
                            {/* Progress bar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="caption" sx={{ minWidth: 35 }}>
                                {formatAudioTime(audioProgress)}
                              </Typography>
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 4,
                                  bgcolor: 'grey.300',
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  position: 'relative'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (audioRef.current && audioDuration) {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const percent = (e.clientX - rect.left) / rect.width;
                                    const newTime = percent * audioDuration;
                                    audioRef.current.currentTime = newTime;
                                    setAudioProgress(newTime);
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    height: '100%',
                                    bgcolor: 'primary.main',
                                    borderRadius: 2,
                                    width: `${audioDuration ? (audioProgress / audioDuration) * 100 : 0}%`
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" sx={{ minWidth: 35 }}>
                                {formatAudioTime(audioDuration)}
                              </Typography>
                            </Box>

                            {/* Control buttons */}
                            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                              <Tooltip title="Back 10s">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); skipAudio(-10); }}>
                                  <Replay10Icon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Forward 10s">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); skipAudio(10); }}>
                                  <Forward10Icon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={`Speed: ${playbackSpeed}x`}>
                                <Chip
                                  size="small"
                                  label={`${playbackSpeed}x`}
                                  onClick={(e) => { e.stopPropagation(); changePlaybackSpeed(); }}
                                  sx={{
                                    cursor: 'pointer',
                                    minWidth: 45,
                                    '&:hover': { bgcolor: 'primary.light' }
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </TabPanel>

              {/* Callbacks Tab */}
              <TabPanel value={mainTab} index={2}>
                {callbacks.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PhoneIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No pending callbacks</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {callbacks.map((cb) => (
                      <ListItem
                        key={cb.id}
                        button
                        onClick={() => handleCustomerSearch(cb.from_number)}
                        sx={{
                          borderBottom: '1px solid #f0f0f0',
                          '&:hover': { bgcolor: '#f5f7fa' },
                          cursor: 'pointer'
                        }}
                        secondaryAction={
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {cb.recording_url && (
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); playRecording(cb, cb.talk_duration); }}
                                sx={{
                                  bgcolor: playingAudio === cb.recording_url ? 'primary.main' : 'transparent',
                                  color: playingAudio === cb.recording_url ? 'white' : 'primary.main',
                                  '&:hover': { bgcolor: 'primary.light' }
                                }}
                              >
                                {playingAudio === cb.recording_url ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                              </IconButton>
                            )}
                            <Tooltip title="Mark as completed">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await callCentreService.completeCallback(cb.id);
                                    setCallbacks(prev => prev.filter(c => c.id !== cb.id));
                                  } catch (err) {
                                    console.error('[Dashboard] Error completing callback:', err);
                                  }
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setActiveCallbackId(cb.id); handleClickToCall(cb.from_number, cb.to_number); }}
                              color="success"
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                width: 28, height: 28, borderRadius: '50%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                bgcolor: '#e8f5e9', color: '#2e7d32'
                              }}>
                                <CallReceivedIcon sx={{ fontSize: 16 }} />
                              </Box>
                              {cb.phone_country_code && (
                                <Chip
                                  size="small"
                                  label={cb.phone_country_code}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                                />
                              )}
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatPhone(cb.from_number)}
                              </Typography>
                              <CallbackWindowBadge phone={cb.from_number} />
                              {cb.customer_name && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  {cb.customer_name}
                                </Typography>
                              )}
                              {cb.edesk_ticket_id && (
                                <Tooltip title={`eDesk Ticket #${cb.edesk_ticket_id}`}>
                                  <ConfirmationNumberIcon
                                    sx={{ fontSize: 14, color: 'success.main', cursor: 'pointer' }}
                                    onClick={(e) => { e.stopPropagation(); window.open(`https://dashboard-1.edesk.com/crm/view/${cb.edesk_ticket_id}`, '_blank'); }}
                                  />
                                </Tooltip>
                              )}
                              {cb.talk_duration > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {Math.floor(cb.talk_duration / 60)}:{String(cb.talk_duration % 60).padStart(2, '0')}
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(cb.requested_at)}
                              </Typography>
                              {cb.transcript && (
                                <Chip
                                  size="small"
                                  label="📝"
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              )}
                              {cb.notes && (
                                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {cb.notes}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </TabPanel>

              {/* Pending Calls Tab */}
              <TabPanel value={mainTab} index={3}>
                {pendingCalls.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PhoneMissedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No pending calls</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {pendingCalls.map((call) => {
                      const isExpanded = expandedCallId === call.twilio_call_sid;
                      return (
                      <Box key={call.call_id || call.twilio_call_sid}>
                        <ListItem
                          button
                          onClick={() => handleCustomerSearch(call.from_number)}
                          sx={{
                            borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0',
                            '&:hover': { bgcolor: '#f5f7fa' },
                            cursor: 'pointer',
                            bgcolor: isExpanded ? '#f5f7fa' : 'transparent'
                          }}
                          secondaryAction={
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleExpandCall(call.twilio_call_sid, call.call_id, isExpanded); }}
                                sx={{ color: 'text.secondary' }}
                              >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                              {call.recording_url && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); playRecording(call, call.talk_duration); }}
                                  sx={{
                                    bgcolor: playingAudio === call.recording_url ? 'primary.main' : 'transparent',
                                    color: playingAudio === call.recording_url ? 'white' : 'primary.main',
                                    '&:hover': { bgcolor: 'primary.light' }
                                  }}
                                >
                                  {playingAudio === call.recording_url ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleClickToCall(call.from_number, call.to_number); }}
                                color="success"
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                              <Tooltip title="Mark as completed">
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await callCentreService.resolvePendingCall(call.call_id || call.id);
                                      setPendingCalls(prev => prev.filter(c => (c.call_id || c.id) !== (call.call_id || call.id)));
                                    } catch (err) {
                                      console.error('[Dashboard] Error resolving pending call:', err);
                                    }
                                  }}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                  width: 28, height: 28, borderRadius: '50%', display: 'flex',
                                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                  bgcolor: '#e8f5e9', color: '#2e7d32'
                                }}>
                                  <CallReceivedIcon sx={{ fontSize: 16 }} />
                                </Box>
                                {call.phone_country_code && (
                                  <Chip
                                    size="small"
                                    label={call.phone_country_code}
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                                  />
                                )}
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatPhone(call.from_number)}
                                </Typography>
                                <CallbackWindowBadge phone={call.from_number} />
                                <Chip
                                  size="small"
                                  label={call.status}
                                  color="error"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                                {call.lifetime_value > 0 && call.lifetime_value_currency && (
                                  <Chip
                                    size="small"
                                    label={formatCurrency(call.lifetime_value, call.lifetime_value_currency)}
                                    color="success"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                                {call.order_count > 0 && (
                                  <Chip
                                    size="small"
                                    label={`${call.order_count} orders`}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                                {call.edesk_ticket_id && (
                                  <Tooltip title={`eDesk Ticket #${call.edesk_ticket_id}`}>
                                    <ConfirmationNumberIcon
                                      sx={{ fontSize: 14, color: 'success.main', cursor: 'pointer' }}
                                      onClick={(e) => { e.stopPropagation(); window.open(`https://dashboard-1.edesk.com/crm/view/${call.edesk_ticket_id}`, '_blank'); }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(call.started_at)}
                                </Typography>
                                {call.agent_name && (
                                  <Chip
                                    size="small"
                                    label={call.agent_name.split(' ')[0]}
                                    variant="outlined"
                                    sx={{ height: 16, fontSize: '0.6rem' }}
                                  />
                                )}
                                {call.transcript && (
                                  <Chip
                                    size="small"
                                    label="📝"
                                    sx={{ height: 16, fontSize: '0.6rem' }}
                                  />
                                )}
                                {Array.isArray(call.tags) && call.tags.map((tag, i) => (
                                  <Chip
                                    key={i}
                                    size="small"
                                    icon={<TagIcon sx={{ fontSize: '0.6rem !important' }} />}
                                    label={tag}
                                    sx={{ height: 16, fontSize: '0.6rem', maxWidth: 80 }}
                                  />
                                ))}
                              </Box>
                            }
                          />
                        </ListItem>
                        {/* Expanded section for transcript, tags, notes */}
                        {isExpanded && (
                          <Box sx={{ px: 2, pb: 2, bgcolor: '#f5f7fa', borderBottom: '1px solid #f0f0f0' }}>
                            {call.recording_url && playingAudio === call.recording_url && (
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: 35 }}>
                                    {formatAudioTime(audioProgress)}
                                  </Typography>
                                  <Box
                                    sx={{ flex: 1, height: 4, bgcolor: 'grey.300', borderRadius: 2, cursor: 'pointer', position: 'relative' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (audioRef.current && audioDuration) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const percent = (e.clientX - rect.left) / rect.width;
                                        const newTime = percent * audioDuration;
                                        audioRef.current.currentTime = newTime;
                                        setAudioProgress(newTime);
                                      }
                                    }}
                                  >
                                    <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', bgcolor: 'primary.main', borderRadius: 2, width: `${audioDuration ? (audioProgress / audioDuration) * 100 : 0}%` }} />
                                  </Box>
                                  <Typography variant="caption" sx={{ minWidth: 35 }}>
                                    {formatAudioTime(audioDuration)}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                  <Tooltip title="Back 10s">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); skipAudio(-10); }}>
                                      <Replay10Icon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Forward 10s">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); skipAudio(10); }}>
                                      <Forward10Icon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Chip
                                    size="small"
                                    label={`${playbackSpeed}x`}
                                    onClick={(e) => { e.stopPropagation(); changePlaybackSpeed(); }}
                                    sx={{ cursor: 'pointer', minWidth: 45 }}
                                  />
                                </Stack>
                              </Box>
                            )}
                            {call.transcript ? (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  Transcript
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.primary', fontSize: '0.85rem' }}>
                                  {call.transcript}
                                </Typography>
                              </Box>
                            ) : call.transcript_status === 'pending' ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Transcript processing...
                              </Typography>
                            ) : call.recording_url ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No transcript available
                              </Typography>
                            ) : null}
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Tags
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                {(callTags[call.call_id] || []).map((t) => (
                                  <Chip
                                    key={t.id}
                                    size="small"
                                    label={t.tag}
                                    onDelete={() => handleRemoveTag(call.call_id, t.id, t.tag)}
                                    sx={{ height: 22, fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={popularTags}
                                inputValue={newTagInput}
                                onInputChange={(e, val) => {
                                  setNewTagInput(val);
                                  if (val.length >= 1) loadPopularTags(val);
                                }}
                                onChange={(e, val) => {
                                  if (val) { handleAddTag(call.call_id, val); setNewTagInput(''); }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Add tag..."
                                    variant="outlined"
                                    size="small"
                                    sx={{ maxWidth: 200 }}
                                  />
                                )}
                                disabled={addingTag}
                              />
                            </Box>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Notes
                              </Typography>
                              {(callNotes[call.call_id] || []).map((note) => (
                                <Box key={note.id} sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{note.note_text}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {note.agent_name || 'Agent'} &middot; {formatDate(note.created_at)}
                                    </Typography>
                                  </Box>
                                  <IconButton size="small" onClick={() => handleDeleteNote(call.call_id, note.id)} sx={{ mt: -0.5, opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              ))}
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <TextField
                                  size="small"
                                  placeholder="Add a note..."
                                  value={newNoteText}
                                  onChange={(e) => setNewNoteText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleAddNote(call.call_id);
                                    }
                                  }}
                                  sx={{ flex: 1 }}
                                  multiline
                                  maxRows={3}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleAddNote(call.call_id)}
                                  disabled={addingNote || !newNoteText.trim()}
                                  sx={{ minWidth: 'auto', px: 2 }}
                                >
                                  {addingNote ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );})}
                  </List>
                )}
              </TabPanel>
            </Box>
          </Paper>

          {/* Order Builder */}
          <Paper sx={{ p: 2.5 }} elevation={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CartIcon fontSize="small" />
              Create Order
            </Typography>

            {/* Product Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search products..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleProductSearch()}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleProductSearch} disabled={isSearchingProducts || !selectedBrand}>
                      {isSearchingProducts ? <CircularProgress size={20} /> : <SearchIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Product Results */}
            {products.length > 0 && (
              <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                {products.slice(0, 5).map((product) => (
                  <Card key={product.id} sx={{ mb: 1 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {product.title}
                      </Typography>
                      {product.variants.slice(0, 3).map((variant) => (
                        <Box
                          key={variant.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 0.5
                          }}
                        >
                          <Typography variant="caption">
                            {variant.title} - {formatCurrency(variant.price)}
                          </Typography>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => addToCart(product, variant)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Cart */}
            {cart.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Cart ({cart.length} items)
                </Typography>

                <List dense>
                  {cart.map((item, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={item.productTitle}
                        secondary={`${item.variantTitle} - ${formatCurrency(item.price)} x ${item.quantity}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton size="small" onClick={() => updateCartQuantity(idx, -1)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => updateCartQuantity(idx, 1)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>

                <Typography variant="h6" sx={{ mt: 1 }}>
                  Subtotal: {formatCurrency(getCartTotal())}
                </Typography>

                {/* Discount Code Section */}
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Discount
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Discount code (e.g., SAVE10)"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={discountType}
                        onChange={(e) => setDiscountType(e.target.value)}
                      >
                        <MenuItem value="percentage">%</MenuItem>
                        <MenuItem value="fixed_amount">$</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      placeholder={discountType === 'percentage' ? '10' : '5.00'}
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      sx={{ flex: 1 }}
                      type="number"
                      inputProps={{ min: 0, step: discountType === 'percentage' ? 1 : 0.01 }}
                    />
                  </Box>
                  {discountValue && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                      {discountType === 'percentage'
                        ? `${discountValue}% discount will be applied`
                        : `$${discountValue} discount will be applied`}
                    </Typography>
                  )}
                </Box>

                {/* Customer Address Info */}
                {customer?.shippingAddress && (
                  <Alert severity="success" sx={{ mt: 1 }} icon={<CheckIcon />}>
                    <Typography variant="caption">
                      <strong>Shipping address from previous order:</strong><br/>
                      {customer.shippingAddress.firstName} {customer.shippingAddress.lastName}<br/>
                      {customer.shippingAddress.address1}
                      {customer.shippingAddress.address2 && `, ${customer.shippingAddress.address2}`}<br/>
                      {customer.shippingAddress.city}, {customer.shippingAddress.provinceCode || customer.shippingAddress.province} {customer.shippingAddress.zip}<br/>
                      {customer.shippingAddress.country}
                    </Typography>
                  </Alert>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={handleCreateOrder}
                  disabled={sendingOrder || !customer || customer.notFound}
                  startIcon={sendingOrder ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  Create Draft Order
                </Button>

                {(!customer || customer.notFound) && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    Search for a customer first to create an order
                  </Typography>
                )}
              </>
            )}
          </Paper>
        </Box>

        {/* Right Column - Call Recording & Notes */}
        <Box sx={{ minWidth: 0 }}>
          <Paper sx={{ p: 2.5, mb: 2 }} elevation={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon fontSize="small" />
              Call Activity
            </Typography>

            {/* Quick Stats Summary */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 45%', textAlign: 'center', p: 1, bgcolor: '#f5f7fa', borderRadius: 1, minWidth: 80 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatDuration(Math.round(callStats?.avg_talk_duration || 0))}</Typography>
                <Typography variant="caption" color="text.secondary">Avg Talk</Typography>
              </Box>
              <Box sx={{ flex: '1 1 45%', textAlign: 'center', p: 1, bgcolor: '#f5f7fa', borderRadius: 1, minWidth: 80 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{callStats?.inbound_calls || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Inbound</Typography>
              </Box>
            </Box>

            {/* Recent Call Recordings */}
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>
              Recent Recordings
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {calls.filter(c => c.recording_url).slice(0, 5).length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.disabled">No recordings yet</Typography>
                </Box>
              ) : (
                calls.filter(c => c.recording_url).slice(0, 5).map((call) => (
                  <Box
                    key={call.call_id}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      bgcolor: playingAudio === call.recording_url ? 'primary.lighter' : '#f5f7fa',
                      borderRadius: 1,
                      border: playingAudio === call.recording_url ? '1px solid' : 'none',
                      borderColor: 'primary.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {formatPhone(call.direction === 'outbound' ? call.to_number : call.from_number)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDuration(call.talk_duration)} • {formatDate(call.started_at)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => playRecording(call, call.talk_duration)}
                        sx={{
                          bgcolor: playingAudio === call.recording_url ? 'primary.main' : 'primary.light',
                          color: playingAudio === call.recording_url ? 'white' : 'primary.main',
                          '&:hover': { bgcolor: 'primary.main', color: 'white' }
                        }}
                      >
                        {playingAudio === call.recording_url ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                      </IconButton>
                    </Box>

                    {/* Audio Controls - Show when playing */}
                    {playingAudio === call.recording_url && (
                      <Box sx={{ mt: 1.5 }}>
                        {/* Progress bar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ minWidth: 35 }}>
                            {formatAudioTime(audioProgress)}
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: 4,
                              bgcolor: 'grey.300',
                              borderRadius: 2,
                              cursor: 'pointer',
                              position: 'relative'
                            }}
                            onClick={(e) => {
                              if (audioRef.current && audioDuration) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const percent = (e.clientX - rect.left) / rect.width;
                                const newTime = percent * audioDuration;
                                audioRef.current.currentTime = newTime;
                                setAudioProgress(newTime);
                              }
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                height: '100%',
                                bgcolor: 'primary.main',
                                borderRadius: 2,
                                width: `${audioDuration ? (audioProgress / audioDuration) * 100 : 0}%`
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ minWidth: 35 }}>
                            {formatAudioTime(audioDuration)}
                          </Typography>
                        </Box>

                        {/* Control buttons */}
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          <Tooltip title="Back 10s">
                            <IconButton size="small" onClick={() => skipAudio(-10)}>
                              <Replay10Icon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Forward 10s">
                            <IconButton size="small" onClick={() => skipAudio(10)}>
                              <Forward10Icon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`Speed: ${playbackSpeed}x`}>
                            <Chip
                              size="small"
                              label={`${playbackSpeed}x`}
                              onClick={changePlaybackSpeed}
                              sx={{
                                cursor: 'pointer',
                                minWidth: 45,
                                '&:hover': { bgcolor: 'primary.light' }
                              }}
                            />
                          </Tooltip>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Paper>

          {/* Agent Status Card */}
          <Paper sx={{ p: 2.5 }} elevation={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" />
                Agent Status
              </Typography>
              {(hasCallCentreAccess || agentInfo?.isAdmin || agentInfo?.hasCallCentreAccess) && (
                <Tooltip title="Manage Agents">
                  <IconButton size="small" onClick={() => setAgentManagementOpen(true)}>
                    <GroupAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: 48, height: 48, bgcolor: 'success.main' }}>
                {(agentInfo?.name || agentInfo?.email || 'A').charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {agentInfo?.name || agentInfo?.email?.split('@')[0] || 'Agent'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                  <Typography variant="caption" color="success.main">Available</Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>Selected Brand</Typography>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedBrand?.name || 'None'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Assigned Brands</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{agentInfo?.assignedBrands?.length || agentInfo?.brands?.length || 0}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">Push Notifications</Typography>
              <Chip
                size="small"
                label={pushEnabled ? 'Enabled' : 'Disabled'}
                color={pushEnabled ? 'success' : 'default'}
                sx={{ height: 20 }}
              />
            </Box>

            {/* Mobile App Setup Section */}
            <Divider sx={{ my: 2 }} />

            <Box
              onClick={() => setMobileSetupExpanded(!mobileSetupExpanded)}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <MobileIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Mobile App Setup
                </Typography>
              </Stack>
              {mobileSetupExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>

            {mobileSetupExpanded && (
              <Box sx={{ mt: 2 }}>
                {loadingSipQr ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>Loading credentials...</Typography>
                  </Box>
                ) : (
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      href="https://storage.googleapis.com/excthumbs/downloads/superchargeme-mobile.apk"
                      target="_blank"
                      sx={{ mb: 1.5, textTransform: 'none', fontWeight: 600 }}
                    >
                      Download Android App (.apk)
                    </Button>

                    <Box sx={{ textAlign: 'left', mb: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        How to Install:
                      </Typography>
                      <Box component="ol" sx={{ m: 0, pl: 2, '& li': { fontSize: '0.7rem', mb: 0.25 } }}>
                        <li>Download the APK on your Android phone</li>
                        <li>Open the file &amp; allow installation from this source</li>
                        <li>Open the app and sign in with your SIP credentials below</li>
                      </Box>
                    </Box>

                    {sipCredentials ? (
                      <Box sx={{ textAlign: 'left', bgcolor: '#f5f7fa', p: 1.5, borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                          Your SIP Credentials:
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                          Username: {sipCredentials.username}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                          Password: {sipCredentials.password}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          No SIP credentials configured
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Box>
        </Box>
      </Box>

      {/* Agent Management Dialog */}
      <Dialog
        open={agentManagementOpen}
        onClose={() => setAgentManagementOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Manage Agent Assignments</Typography>
            <IconButton onClick={() => setAgentManagementOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingAgentData ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Add New Assignment */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f7fa' }}>
                <Typography variant="subtitle2" gutterBottom>Add Agent to Brand</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Select Agent</InputLabel>
                    <Select
                      value={selectedAgent}
                      onChange={(e) => setSelectedAgent(e.target.value)}
                      label="Select Agent"
                    >
                      {slackUsers.map(user => (
                        <MenuItem key={user.slackUserId} value={user.slackUserId}>
                          {user.name || user.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Select Brand</InputLabel>
                    <Select
                      value={selectedBrandToAdd}
                      onChange={(e) => setSelectedBrandToAdd(e.target.value)}
                      label="Select Brand"
                    >
                      {allBrands.map(brand => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={handleAddAssignment}
                    disabled={!selectedAgent || !selectedBrandToAdd || addingAssignment}
                    startIcon={addingAssignment ? <CircularProgress size={16} /> : <AddIcon />}
                  >
                    Add
                  </Button>
                </Stack>
              </Paper>

              {/* Current Assignments — with filters and sorting */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Current Assignments</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Filter Brand</InputLabel>
                    <Select
                      value={assignmentFilterBrand}
                      onChange={(e) => setAssignmentFilterBrand(e.target.value)}
                      label="Filter Brand"
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {[...new Set(agentAssignments.map(a => a.brand_name))].sort().map(name => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Filter Agent</InputLabel>
                    <Select
                      value={assignmentFilterAgent}
                      onChange={(e) => setAssignmentFilterAgent(e.target.value)}
                      label="Filter Agent"
                    >
                      <MenuItem value="">All Agents</MenuItem>
                      {[...new Set(agentAssignments.map(a => {
                        const u = slackUsers.find(u => u.slackUserId === a.slack_user_id);
                        return u?.name || a.slack_user_id;
                      }))].sort().map(name => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={assignmentFilterStatus}
                      onChange={(e) => setAssignmentFilterStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="">All</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { key: 'agent', label: 'Agent' },
                      { key: 'brand', label: 'Brand' },
                      { key: 'priority', label: 'Priority' },
                      { key: 'status', label: 'Status' },
                    ].map(col => (
                      <TableCell
                        key={col.key}
                        sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600, '&:hover': { color: 'primary.main' } }}
                        onClick={() => {
                          if (assignmentSortBy === col.key) {
                            setAssignmentSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
                          } else {
                            setAssignmentSortBy(col.key);
                            setAssignmentSortDir('asc');
                          }
                        }}
                      >
                        {col.label} {assignmentSortBy === col.key ? (assignmentSortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                    ))}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Filter
                    let filtered = agentAssignments.map(a => ({
                      ...a,
                      _agentName: (slackUsers.find(u => u.slackUserId === a.slack_user_id))?.name || a.slack_user_id
                    }));
                    if (assignmentFilterBrand) filtered = filtered.filter(a => a.brand_name === assignmentFilterBrand);
                    if (assignmentFilterAgent) filtered = filtered.filter(a => a._agentName === assignmentFilterAgent);
                    if (assignmentFilterStatus === 'active') filtered = filtered.filter(a => a.is_active);
                    if (assignmentFilterStatus === 'inactive') filtered = filtered.filter(a => !a.is_active);
                    // Sort
                    filtered.sort((a, b) => {
                      let va, vb;
                      if (assignmentSortBy === 'agent') { va = a._agentName.toLowerCase(); vb = b._agentName.toLowerCase(); }
                      else if (assignmentSortBy === 'brand') { va = (a.brand_name || '').toLowerCase(); vb = (b.brand_name || '').toLowerCase(); }
                      else if (assignmentSortBy === 'priority') { va = a.priority; vb = b.priority; }
                      else if (assignmentSortBy === 'status') { va = a.is_active ? 0 : 1; vb = b.is_active ? 0 : 1; }
                      else { va = (a.brand_name || '').toLowerCase(); vb = (b.brand_name || '').toLowerCase(); }
                      if (va < vb) return assignmentSortDir === 'asc' ? -1 : 1;
                      if (va > vb) return assignmentSortDir === 'asc' ? 1 : -1;
                      return 0;
                    });
                    if (filtered.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                            No agent assignments found
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return filtered.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell>{assignment._agentName}</TableCell>
                        <TableCell>{assignment.brand_name}</TableCell>
                        <TableCell>{assignment.priority}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={assignment.is_active ? 'Active' : 'Inactive'}
                            color={assignment.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveAssignment(assignment.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgentManagementOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* All Calls Dialog */}
      <Dialog
        open={allCallsDialogOpen}
        onClose={() => setAllCallsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">All Calls</Typography>
            <IconButton onClick={() => setAllCallsDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {/* Filters Row */}
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Status Filter */}
            <Stack direction="row" spacing={0.5}>
              <Chip
                label="All"
                onClick={() => handleAllCallsFilterChange('all')}
                color={allCallsFilter === 'all' ? 'primary' : 'default'}
                size="small"
              />
              <Chip
                label="Completed"
                onClick={() => handleAllCallsFilterChange('completed')}
                color={allCallsFilter === 'completed' ? 'success' : 'default'}
                size="small"
              />
              <Chip
                label="Missed"
                onClick={() => handleAllCallsFilterChange('missed')}
                color={allCallsFilter === 'missed' ? 'error' : 'default'}
                size="small"
              />
            </Stack>
            {/* Agent Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Agent</InputLabel>
              <Select
                value={allCallsAgentFilter}
                label="Agent"
                onChange={(e) => handleAllCallsAgentChange(e.target.value)}
              >
                <MenuItem value="">All Agents</MenuItem>
                {agentsList.map(agent => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name.split(' ')[0]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Phone Search */}
            <TextField
              size="small"
              placeholder="Search phone..."
              value={allCallsPhoneSearch}
              onChange={(e) => handleAllCallsPhoneSearch(e.target.value)}
              sx={{ minWidth: 180 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {/* Page Size */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Show</InputLabel>
              <Select
                value={allCallsLimit}
                label="Show"
                onChange={(e) => handleAllCallsLimitChange(e.target.value)}
              >
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {allCallsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sortedAllCalls.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PhoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No calls found</Typography>
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                    onClick={() => handleSortChange('direction')}
                  >
                    Direction {allCallsSortField === 'direction' && (allCallsSortDir === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                    onClick={() => handleSortChange('agent_name')}
                  >
                    Agent {allCallsSortField === 'agent_name' && (allCallsSortDir === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                    onClick={() => handleSortChange('status')}
                  >
                    Status {allCallsSortField === 'status' && (allCallsSortDir === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                    onClick={() => handleSortChange('talk_duration')}
                  >
                    Duration {allCallsSortField === 'talk_duration' && (allCallsSortDir === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                    onClick={() => handleSortChange('started_at')}
                  >
                    Date {allCallsSortField === 'started_at' && (allCallsSortDir === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAllCalls.map((call) => {
                  const customerPhone = call.direction === 'outbound' ? call.to_number : call.from_number;
                  const isExpanded = allCallsExpandedId === call.twilio_call_sid;
                  return (
                    <React.Fragment key={call.twilio_call_sid}>
                      <TableRow
                        hover
                        sx={{ bgcolor: isExpanded ? '#f5f7fa' : 'inherit' }}
                      >
                        <TableCell>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '50%', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center',
                            bgcolor: call.direction === 'outbound' ? '#e3f2fd' : '#e8f5e9',
                            color: call.direction === 'outbound' ? '#1565c0' : '#2e7d32'
                          }}>
                            {call.direction === 'outbound'
                              ? <CallMadeIcon sx={{ fontSize: 16 }} />
                              : <CallReceivedIcon sx={{ fontSize: 16 }} />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            onClick={() => {
                              setAllCallsDialogOpen(false);
                              handleCustomerSearch(customerPhone);
                            }}
                          >
                            {formatPhone(customerPhone)}
                          </Typography>
                          <CallbackWindowBadge phone={customerPhone} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {call.agent_name ? call.agent_name.split(' ')[0] : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={call.status}
                            color={call.status === 'completed' ? 'success' : call.status === 'no-answer' ? 'error' : 'warning'}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {call.talk_duration > 0
                              ? `${Math.floor(call.talk_duration / 60)}:${String(call.talk_duration % 60).padStart(2, '0')}`
                              : '-'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(call.started_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                            {Array.isArray(call.tags) && call.tags.map((tag, i) => (
                              <Chip
                                key={i}
                                size="small"
                                label={tag}
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => handleExpandAllCall(call.twilio_call_sid, call.call_id, isExpanded)}
                                sx={{ color: call.transcript ? 'primary.main' : 'text.secondary' }}
                              >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            {call.recording_url && (
                              <IconButton
                                size="small"
                                onClick={() => playRecording(call, call.talk_duration)}
                                sx={{
                                  bgcolor: playingAudio === call.recording_url ? 'primary.main' : 'transparent',
                                  color: playingAudio === call.recording_url ? 'white' : 'primary.main'
                                }}
                              >
                                {playingAudio === call.recording_url ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleClickToCall(customerPhone)}
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {/* Expanded row for transcript */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ bgcolor: '#f5f7fa', py: 2 }}>
                            {/* Audio player if playing */}
                            {call.recording_url && playingAudio === call.recording_url && (
                              <Box sx={{ mb: 2, maxWidth: 400 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: 35 }}>
                                    {formatAudioTime(audioProgress)}
                                  </Typography>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      height: 4,
                                      bgcolor: 'grey.300',
                                      borderRadius: 2,
                                      cursor: 'pointer',
                                      position: 'relative'
                                    }}
                                    onClick={(e) => {
                                      if (audioRef.current && audioDuration) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const percent = (e.clientX - rect.left) / rect.width;
                                        audioRef.current.currentTime = percent * audioDuration;
                                        setAudioProgress(percent * audioDuration);
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        height: '100%',
                                        bgcolor: 'primary.main',
                                        borderRadius: 2,
                                        width: `${audioDuration ? (audioProgress / audioDuration) * 100 : 0}%`
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ minWidth: 35 }}>
                                    {formatAudioTime(audioDuration)}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={`${playbackSpeed}x`}
                                    onClick={changePlaybackSpeed}
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                  />
                                </Box>
                              </Box>
                            )}
                            {/* Transcript */}
                            {call.transcript ? (
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                  📝 Transcript
                                </Typography>
                                <Box sx={{
                                  bgcolor: 'white',
                                  p: 2,
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                  maxHeight: 300,
                                  overflow: 'auto'
                                }}>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                                    {call.transcript}
                                  </Typography>
                                </Box>
                              </Box>
                            ) : call.transcript_status === 'pending' ? (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                ⏳ Transcript is being processed...
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No transcript available for this call.
                              </Typography>
                            )}
                            {/* Tags Section */}
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Tags</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                {(callTags[call.call_id] || []).map((t) => (
                                  <Chip
                                    key={t.id}
                                    size="small"
                                    label={t.tag}
                                    onDelete={() => handleRemoveTag(call.call_id, t.id, t.tag)}
                                    sx={{ height: 24, fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={popularTags}
                                inputValue={newTagInput}
                                onInputChange={(e, val) => {
                                  setNewTagInput(val);
                                  if (val.length >= 1) loadPopularTags(val);
                                }}
                                onChange={(e, val) => {
                                  if (val) { handleAddTag(call.call_id, val); setNewTagInput(''); }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Add tag..."
                                    variant="outlined"
                                    size="small"
                                    sx={{ maxWidth: 220 }}
                                  />
                                )}
                                disabled={addingTag}
                              />
                            </Box>
                            {/* Notes Section */}
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Notes</Typography>
                              {(callNotes[call.call_id] || []).map((note) => (
                                <Box key={note.id} sx={{ mb: 1, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2">{note.note_text}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {note.agent_name || 'Agent'} &middot; {formatDate(note.created_at)}
                                    </Typography>
                                  </Box>
                                  <IconButton size="small" onClick={() => handleDeleteNote(call.call_id, note.id)} sx={{ mt: -0.5, opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              ))}
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <TextField
                                  size="small"
                                  placeholder="Add a note..."
                                  value={newNoteText}
                                  onChange={(e) => setNewNoteText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleAddNote(call.call_id);
                                    }
                                  }}
                                  sx={{ flex: 1 }}
                                  multiline
                                  maxRows={3}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleAddNote(call.call_id)}
                                  disabled={addingNote || !newNoteText.trim()}
                                  sx={{ minWidth: 'auto', px: 2 }}
                                >
                                  {addingNote ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                                </Button>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {sortedAllCalls.length} of {allCallsTotal} calls
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              disabled={allCallsPage <= 1}
              onClick={() => handleAllCallsPageChange(allCallsPage - 1)}
              startIcon={<NavigateBeforeIcon />}
            >
              Prev
            </Button>
            <Typography variant="body2" sx={{ px: 1 }}>
              Page {allCallsPage} of {Math.ceil(allCallsTotal / allCallsLimit) || 1}
            </Typography>
            <Button
              size="small"
              disabled={allCallsPage >= Math.ceil(allCallsTotal / allCallsLimit)}
              onClick={() => handleAllCallsPageChange(allCallsPage + 1)}
              endIcon={<NavigateNextIcon />}
            >
              Next
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Draft Order Dialog */}
      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Draft Order Created</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {draftOrder && (
            <>
              <Typography variant="h6" gutterBottom>
                {draftOrder.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Total: {formatCurrency(draftOrder.totalPrice, draftOrder.currency)}
              </Typography>

              <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Invoice URL:</strong>
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {draftOrder.invoiceUrl}
                </Typography>
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => copyToClipboard(draftOrder.invoiceUrl)}
                  sx={{ mt: 1 }}
                >
                  Copy URL
                </Button>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                Send this payment link to the customer via SMS or share the URL directly.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleSendSms}
            disabled={sendingOrder}
            startIcon={sendingOrder ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Send via SMS
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
};

export default CallCentreDashboard;
